import React, { useState, useEffect, useMemo } from 'react';
import { EditorContent } from '@tiptap/react';
import { 
  PenTool, Plus, Edit3, Trash2, Fingerprint, Loader2, CheckCircle2, BarChart2, RefreshCw, Activity,
  Bold, Italic, Underline, List, ListOrdered, Quote, Code, AlertCircle, Type, Heart, Upload, BookOpen,
  Minus, Undo, Redo, Settings, X, Hash, Clock, Inbox, ExternalLink, Calendar, Search, CheckSquare, Square, ToggleLeft, ToggleRight, ChevronDown, ChevronLeft, ChevronRight, Eye, Download, Feather,
  Database, UploadCloud, History, FileJson, RotateCcw, Lightbulb, SpellCheck
} from 'lucide-react';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import QuoteEditorModal from '../QuoteEditorModal';
import PoemEditorModal from '../PoemEditorModal';
import DiaryEditorModal from '../DiaryEditorModal';
import GrammarCheckPanel from '../GrammarCheckPanel';
import MindMapEditor from '../MindMapEditor';
import { collection, getDocs, query, orderBy, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../../hooks/useFirebase';
import { useGoogleDrive } from '../../hooks/useGoogleDrive';
import Toast from '../Toast';
import LocationMap from '../LocationMap'; // Make sure to import the new map

// Robust CSV parser to handle commas properly inside double quotes (Notion exports)
const parseCSV = (str) => {
  const arr = [];
  let quote = false;
  let row = 0, col = 0;
  for (let c = 0; c < str.length; c++) {
    let cc = str[c], nc = str[c+1];
    arr[row] = arr[row] || [];
    arr[row][col] = arr[row][col] || '';
    if (cc === '"' && quote && nc === '"') { arr[row][col] += cc; ++c; continue; }
    if (cc === '"') { quote = !quote; continue; }
    if (cc === ',' && !quote) { ++col; continue; }
    if (cc === '\r' && nc === '\n' && !quote) { ++row; col = 0; ++c; continue; }
    if (cc === '\n' && !quote) { ++row; col = 0; continue; }
    if (cc === '\r' && !quote) { ++row; col = 0; continue; }
    arr[row][col] += cc;
  }
  return arr;
};

// Robust date parser to handle standard YYYY-MM-DD, ISO, and Notion CSV formats
const safeParseDate = (dateStr) => {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(trimmed + 'T12:00:00');
  }
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
};

const AdminSection = ({
  isAdminLoggedIn, isComposing, isDarkMode, theme, posts, setPosts,
  quotes, setQuotes, poems, setPoems,
  diary, setDiary, submitDiary, updateDiary, diaryEditor,
  ideas, saveIdea, deleteIdea,
  loginError, handleGoogleLogin,
  openNewPostEditor, openEditPostEditor, handleDeleteWithConfirm, 
  adminTab, setAdminTab, showQuoteForm, setShowQuoteForm, 
  quoteText, setQuoteText, quoteAuthor, setQuoteAuthor, quoteDate, setQuoteDate, quoteTags, setQuoteTags, submitQuote, 
  adminStats, updateQuote, editingPostId, composeTitle, setComposeTitle, 
  composeCategory, setComposeCategory, composeDate, setComposeDate, saveStatus, editor,
  closeEditor, handleSavePost,
  triggerSmartSync,
  categories, updateCategories,
  liveStats, bulkDelete,
  executeCommand, handleNavigate,
  submitPoem, updatePoem,
  googleAccessToken, setGoogleAccessToken,
  localDraftAvailable, restoreLocalDraft, clearLocalDraft,
  grammarIssues, isCheckingGrammar,
  handleGrammarCheck, replaceGrammarFix, clearGrammarIssues,
  ignoreGrammarIssue
}) => {
  const [mobileWorkspace, setMobileWorkspace] = useState('hub');

  // Robust utility to format any date input strictly into dd-mm-yyyy format
  const formatDateToDDMMYYYY = (dateInput) => {
    if (!dateInput) return '';
    try {
      let dateObj;
      if (dateInput instanceof Date) {
        dateObj = dateInput;
      } else if (typeof dateInput === 'object' && dateInput.seconds) {
        dateObj = new Date(dateInput.seconds * 1000);
      } else {
        const parsedMs = Date.parse(dateInput);
        if (!isNaN(parsedMs)) {
          dateObj = new Date(parsedMs);
        } else {
          const regex = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;
          if (regex.test(dateInput)) {
            const matches = dateInput.match(regex);
            const day = matches[1].padStart(2, '0');
            const month = matches[2].padStart(2, '0');
            const year = matches[3];
            return `${day}-${month}-${year}`;
          }
          dateObj = new Date(dateInput);
        }
      }

      if (dateObj && !isNaN(dateObj.getTime())) {
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}-${month}-${year}`;
      }
    } catch (e) {
      console.error("Strict date formatting failed for:", dateInput, e);
    }
    return String(dateInput);
  };

  // Helper to extract the last written/created date from collections in strict dd-mm-yyyy format
  const getLastWrittenDate = (items) => {
    if (!items || items.length === 0) return 'No entries yet';
    const newest = items[0];
    const rawDate = newest.date || newest.createdAt;
    return formatDateToDDMMYYYY(rawDate);
  };

  const {
    isBackupLoading,
    backupLogs,
    driveBackups,
    performBackup,
    fetchBackupsList,
    findOrCreateFolder,
    downloadBackupContent
  } = useGoogleDrive();



  // Load Drive Backups List
  useEffect(() => {
    const loadBackups = async () => {
      if (googleAccessToken) {
        try {
          const folderId = await findOrCreateFolder(googleAccessToken);
          if (folderId) {
            await fetchBackupsList(googleAccessToken, folderId);
          }
        } catch (err) {
          console.error("Failed to load backups list:", err);
          if (err.message === 'UNAUTHORIZED_DRIVE_SESSION') {
            setGoogleAccessToken('');
            localStorage.removeItem('google_access_token');
          }
        }
      }
    };
    loadBackups();
  }, [googleAccessToken, fetchBackupsList, findOrCreateFolder, setGoogleAccessToken]);

  // Synchronize mobile workspace view with adminTab
  useEffect(() => {
    if (adminTab && mobileWorkspace !== 'hub' && mobileWorkspace !== adminTab) {
      setMobileWorkspace(adminTab);
    }
  }, [adminTab]);

  const handleConnectDrive = () => {
    handleGoogleLogin();
  };

  const [showEditQuoteForm, setShowEditQuoteForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [editQuoteText, setEditQuoteText] = useState('');
  const [editQuoteAuthor, setEditQuoteAuthor] = useState('');
  const [editQuoteDate, setEditQuoteDate] = useState('');
  const [editQuoteTags, setEditQuoteTags] = useState('');
  const [editorFontSize, setEditorFontSize] = useState(1.375);

  // Poem local states
  const [showPoemForm, setShowPoemForm] = useState(false);
  const [showEditPoemForm, setShowEditPoemForm] = useState(false);
  const [editingPoem, setEditingPoem] = useState(null);
  const [poemTitle, setPoemTitle] = useState('');
  const [poemText, setPoemText] = useState('');
  const [poemAuthor, setPoemAuthor] = useState('');
  const [poemDate, setPoemDate] = useState('');
  const [poemTags, setPoemTags] = useState('');
  
  const [editPoemTitle, setEditPoemTitle] = useState('');
  const [editPoemText, setEditPoemText] = useState('');
  const [editPoemAuthor, setEditPoemAuthor] = useState('');
  const [editPoemDate, setEditPoemDate] = useState('');
  const [editPoemTags, setEditPoemTags] = useState('');

  // Poem Filtering & Pagination States
  const [poemSearchQuery, setPoemSearchQuery] = useState('');
  const [poemSelectedYear, setPoemSelectedYear] = useState('All');
  const [poemSelectedMonth, setPoemSelectedMonth] = useState('All');
  const [isPoemYearOpen, setIsPoemYearOpen] = useState(false);
  const [isPoemMonthOpen, setIsPoemMonthOpen] = useState(false);
  const [poemCurrentPage, setPoemCurrentPage] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [toast, setToast] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [sortBy, setSortBy] = useState('date-desc');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('published');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [analyticsData, setAnalyticsData] = useState([]);
  const [analyticsTimeFilter, setAnalyticsTimeFilter] = useState('all');
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [showAdminVisits, setShowAdminVisits] = useState(false);
  const [analyticsCurrentPage, setAnalyticsCurrentPage] = useState(1);
  const analyticsItemsPerPage = 50;
  const [mapTimeFilter, setMapTimeFilter] = useState('all');
  const [analyticsTypeFilter, setAnalyticsTypeFilter] = useState('all'); // 'all', 'blog', 'quotes'
  const [isMapFilterOpen, setIsMapFilterOpen] = useState(false);
  const [mapRegionFilter, setMapRegionFilter] = useState('all');
  const [isRegionFilterOpen, setIsRegionFilterOpen] = useState(false);

  // Quote Filtering & Pagination
  const [quoteSearchQuery, setQuoteSearchQuery] = useState('');
  const [quoteSelectedYear, setQuoteSelectedYear] = useState('All');
  const [quoteSelectedMonth, setQuoteSelectedMonth] = useState('All');
  const [isQuoteYearOpen, setIsQuoteYearOpen] = useState(false);
  const [isQuoteMonthOpen, setIsQuoteMonthOpen] = useState(false);
  const [quoteCurrentPage, setQuoteCurrentPage] = useState(1);

  // Diary States
  const [showDiaryModal, setShowDiaryModal] = useState(false);
  const [showIdeaEditor, setShowIdeaEditor] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [ideaSaveStatus, setIdeaSaveStatus] = useState('idle');
  const [geminiKey, setGeminiKey] = useState(() => localStorage.getItem('gemini_api_key') || '');
  const [editingDiary, setEditingDiary] = useState(null);
  const [diarySearchQuery, setDiarySearchQuery] = useState('');
  const [diarySelectedYear, setDiarySelectedYear] = useState('All');
  const [diarySelectedMonth, setDiarySelectedMonth] = useState('All');
  const [isDiaryYearOpen, setIsDiaryYearOpen] = useState(false);
  const [isDiaryMonthOpen, setIsDiaryMonthOpen] = useState(false);
  const [diaryCurrentPage, setDiaryCurrentPage] = useState(1);
  const [diaryStatusFilter, setDiaryStatusFilter] = useState('all');
  const [expandedBooks, setExpandedBooks] = useState(new Set());

  // Clear selection when tab changes
  useEffect(() => {
    setSelectedItems(new Set());
    setCurrentPage(1);
    setAnalyticsCurrentPage(1);
    setQuoteCurrentPage(1);
    setDiaryCurrentPage(1);
  }, [adminTab, analyticsTimeFilter, analyticsTypeFilter, showAdminVisits]);

  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreConfirmBackup, setRestoreConfirmBackup] = useState(null);

  // Sparkline data generation (past 7 days visits)
  const sparklineData = useMemo(() => {
    const dailyCounts = Array(7).fill(0);
    const now = new Date();
    
    // Group traffic views excluding admin visits
    const visits = analyticsData.filter(d => d.role !== 'admin');
    
    visits.forEach(v => {
      let visitDate;
      if (v.timestamp && typeof v.timestamp.toDate === 'function') {
        visitDate = v.timestamp.toDate();
      } else if (v.timestamp) {
        visitDate = new Date(v.timestamp);
      } else {
        return;
      }
      
      const diffTime = now.getTime() - visitDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays >= 0 && diffDays < 7) {
        dailyCounts[6 - diffDays]++;
      }
    });
    return dailyCounts;
  }, [analyticsData]);

  // SVG Sparkline Line Renderer
  const renderSparklineSVG = () => {
    if (!sparklineData || sparklineData.length === 0) return null;
    const maxVal = Math.max(...sparklineData, 1);
    const height = 30;
    const width = 120;
    const padding = 2;
    const step = width / (sparklineData.length - 1);
    
    const points = sparklineData.map((val, idx) => {
      const x = idx * step;
      const y = height - padding - ((val / maxVal) * (height - 2 * padding));
      return `${x},${y}`;
    });
    
    const pathD = `M ${points.join(' L ')}`;
    
    return (
      <svg width={width} height={height} className="opacity-60 dark:opacity-80 flex-shrink-0">
        <path
          d={pathD}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-stone-400 dark:text-stone-500"
        />
        <path
          d={pathD}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-stone-400/10 dark:text-stone-500/10 blur-[1px]"
        />
      </svg>
    );
  };

  // Safe Firestore Database Log Pruning Tool
  const pruneOldLogs = async () => {
    setIsSaving(true);
    showToast("Evaluating traffic logs for optimization...", "info");
    try {
      const now = new Date();
      const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 180);
      
      const oldLogs = analyticsData.filter(d => {
        if (!d.timestamp) return false;
        let date;
        if (typeof d.timestamp.toDate === 'function') {
          date = d.timestamp.toDate();
        } else {
          date = new Date(d.timestamp);
        }
        return date < cutoff;
      });
      
      if (oldLogs.length === 0) {
        showToast("Database is healthy. No logs older than 180 days found.", "success");
        return;
      }
      
      let count = 0;
      for (const log of oldLogs) {
        await deleteDoc(doc(db, 'page_views', log.id));
        count++;
      }
      
      setAnalyticsData(prev => prev.filter(d => !oldLogs.some(ol => ol.id === d.id)));
      showToast(`Database optimized! Pruned ${count} old traffic logs.`, "success");
    } catch (e) {
      console.error(e);
      showToast("Failed to prune old logs.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Safe Firestore Database Snapshot Restore Tool
  const handleRestoreBackup = async (backupFile) => {
    setIsRestoring(true);
    showToast("Initiating secure snapshot restore protocol...", "info");
    
    try {
      const downloadRes = await downloadBackupContent(googleAccessToken, backupFile.id);
      if (!downloadRes.success) {
        throw new Error(downloadRes.error || "Failed to download backup snapshot.");
      }
      
      const backupData = downloadRes.data;
      if (!backupData || !backupData.payload) {
        throw new Error("Invalid backup snapshot format. Missing payload.");
      }
      
      const { posts: backupPosts, quotes: backupQuotes, poems: backupPoems, categories: backupCategories } = backupData.payload;
      
      // Overwrite reflections in parallel
      if (backupPosts) {
        showToast("Restoring Reflections...", "info");
        const currentSnap = await getDocs(collection(db, 'posts'));
        await Promise.all(currentSnap.docs.map(d => deleteDoc(doc(db, 'posts', d.id))));
        await Promise.all(backupPosts.map(post => {
          const { id, ...postFields } = post;
          return setDoc(doc(db, 'posts', id), postFields);
        }));
        setPosts(backupPosts);
      }
      
      // Overwrite quotes in parallel
      if (backupQuotes) {
        showToast("Restoring Quotes...", "info");
        const currentSnap = await getDocs(collection(db, 'quotes'));
        await Promise.all(currentSnap.docs.map(d => deleteDoc(doc(db, 'quotes', d.id))));
        await Promise.all(backupQuotes.map(q => {
          const { id, ...quoteFields } = q;
          return setDoc(doc(db, 'quotes', id), quoteFields);
        }));
        setQuotes(backupQuotes);
      }
      
      // Overwrite poems in parallel
      if (backupPoems) {
        showToast("Restoring Poems...", "info");
        const currentSnap = await getDocs(collection(db, 'poems'));
        await Promise.all(currentSnap.docs.map(d => deleteDoc(doc(db, 'poems', d.id))));
        await Promise.all(backupPoems.map(p => {
          const { id, ...poemFields } = p;
          return setDoc(doc(db, 'poems', id), poemFields);
        }));
        setPoems(backupPoems);
      }
      
      // Overwrite categories
      if (backupCategories && updateCategories) {
        await updateCategories(backupCategories);
      }
      
      showToast("Database successfully restored to selected snapshot!", "success");
      setRestoreConfirmBackup(null);
    } catch (e) {
      console.error("Database restore failed:", e);
      showToast(`Restore failed: ${e.message}`, "error");
    } finally {
      setIsRestoring(false);
    }
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, statusFilter, sortBy]);

  useEffect(() => {
    setQuoteCurrentPage(1);
  }, [quoteSearchQuery, quoteSelectedYear, quoteSelectedMonth]);

  const fetchAnalytics = async () => {
    setIsAnalyticsLoading(true);
    try {
      const q = query(collection(db, 'page_views'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      showToast("Failed to load analytics", "error");
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [adminTab]);

  // Calculate candidate count for pruning older than 180 days
  const oldLogsCount = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 180);
    return analyticsData.filter(d => {
      if (!d.timestamp) return false;
      let date;
      if (typeof d.timestamp.toDate === 'function') {
        date = d.timestamp.toDate();
      } else {
        date = new Date(d.timestamp);
      }
      return date < cutoff;
    }).length;
  }, [analyticsData]);

  const filteredAnalytics = useMemo(() => {
    // Filter out admin visits if the toggle is off
    let data = showAdminVisits ? analyticsData : analyticsData.filter(d => d.role !== 'admin');

    // Filter by Blog vs Quote vs Poems
    if (analyticsTypeFilter === 'blog') {
      data = data.filter(d => d.path && d.path.includes('?p='));
    } else if (analyticsTypeFilter === 'quotes') {
      data = data.filter(d => d.path && (d.path.includes('?quote=') || d.path.includes('/quote/')));
    } else if (analyticsTypeFilter === 'poems') {
      data = data.filter(d => d.path && (d.path.includes('?poem=') || d.path.includes('/poem/')));
    }

    if (analyticsTimeFilter === 'all') return data;
    const now = new Date();
    return data.filter(d => {
      if (!d.timestamp || !d.timestamp.toDate) return true; // Keep recent un-synced writes
      const date = d.timestamp.toDate();
      
      if (analyticsTimeFilter === 'today') {
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return date >= startOfDay;
      }
      if (analyticsTimeFilter === 'week') {
        const lastWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        return date >= lastWeek;
      }
      if (analyticsTimeFilter === 'month') {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return date >= lastMonth;
      }
      return true;
    });
  }, [analyticsData, analyticsTimeFilter, analyticsTypeFilter, showAdminVisits]);

  // Independent data filter specifically for the Location Map
  const mapFilteredAnalytics = useMemo(() => {
    let data = showAdminVisits ? analyticsData : analyticsData.filter(d => d.role !== 'admin');
    
    if (analyticsTypeFilter === 'blog') {
      data = data.filter(d => d.path && d.path.includes('?p='));
    } else if (analyticsTypeFilter === 'quotes') {
      data = data.filter(d => d.path && (d.path.includes('?quote=') || d.path.includes('/quote/')));
    } else if (analyticsTypeFilter === 'poems') {
      data = data.filter(d => d.path && (d.path.includes('?poem=') || d.path.includes('/poem/')));
    }

    if (mapRegionFilter === 'my') {
      data = data.filter(d => d.country === 'MY' || d.country === 'Malaysia');
    } else if (mapRegionFilter === 'intl') {
      data = data.filter(d => d.country && d.country !== 'MY' && d.country !== 'Malaysia' && d.country !== 'Unknown');
    }

    if (mapTimeFilter === 'all') return data;
    
    const now = new Date();
    return data.filter(d => {
      if (!d.timestamp || !d.timestamp.toDate) return true;
      const date = d.timestamp.toDate();
      if (mapTimeFilter === 'week') {
        const lastWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        return date >= lastWeek;
      }
      if (mapTimeFilter === 'month') {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return date >= lastMonth;
      }
      return true;
    });
  }, [analyticsData, mapTimeFilter, showAdminVisits, analyticsTypeFilter, mapRegionFilter]);

  const topLocation = useMemo(() => {
    if (!filteredAnalytics.length) return 'Unknown';
    const counts = {};
    filteredAnalytics.forEach(d => {
      const city = d.city && d.city !== 'Unknown' && d.city !== 'undefined' ? `${d.city}, ` : '';
      const country = d.country && d.country !== 'Unknown' && d.country !== 'undefined' ? d.country : 'Unknown';
      const loc = city + country;
      if (loc !== 'Unknown') counts[loc] = (counts[loc] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted.length > 0 ? sorted[0][0] : 'Unknown';
  }, [filteredAnalytics]);

  // Group data dynamically for the Traffic Chart
  const chartData = useMemo(() => {
    if (!filteredAnalytics.length) return [];
    const data = [];
    const now = new Date();
    
    if (analyticsTimeFilter === 'today') {
      const currentHour = now.getHours();
      for (let i = 0; i <= currentHour; i++) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), i);
        data.push({
          label: d.toLocaleTimeString([], { hour: 'numeric', hour12: true }),
          count: 0,
          dateKey: d.getHours()
        });
      }
    } else if (analyticsTimeFilter === 'week') {
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
        data.push({
          label: d.toLocaleDateString([], { weekday: 'short' }),
          count: 0,
          dateKey: d.toDateString()
        });
      }
    } else if (analyticsTimeFilter === 'month') {
       for (let i = 29; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
        data.push({
          label: d.toLocaleDateString([], { month: 'short', day: 'numeric' }),
          count: 0,
          dateKey: d.toDateString()
        });
      }
    } else {
      const groups = {};
      filteredAnalytics.forEach(v => {
         if(!v.timestamp?.toDate) return;
         const d = v.timestamp.toDate();
         const key = d.toDateString();
         if (!groups[key]) {
           groups[key] = { label: d.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' }), count: 0, time: d.getTime() };
         }
         groups[key].count++;
      });
      return Object.values(groups).sort((a,b) => a.time - b.time);
    }

    if (analyticsTimeFilter !== 'all') {
      filteredAnalytics.forEach(v => {
         if(!v.timestamp?.toDate) return;
         const d = v.timestamp.toDate();
         if (analyticsTimeFilter === 'today') {
            const match = data.find(b => b.dateKey === d.getHours() && d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear());
            if (match) match.count++;
         } else {
            const match = data.find(b => b.dateKey === d.toDateString());
            if (match) match.count++;
         }
      });
    }
    return data;
  }, [filteredAnalytics, analyticsTimeFilter]);

  const maxChartValue = Math.max(...chartData.map(d => d.count), 1);

  const uniqueVisitors = useMemo(() => {
    const ids = new Set(filteredAnalytics.map(v => v.visitorId).filter(Boolean));
    return ids.size;
  }, [filteredAnalytics]);

  const totalAnalyticsPages = Math.ceil(filteredAnalytics.length / analyticsItemsPerPage);
  const paginatedAnalytics = useMemo(() => {
    return filteredAnalytics.slice(
      (analyticsCurrentPage - 1) * analyticsItemsPerPage,
      analyticsCurrentPage * analyticsItemsPerPage
    );
  }, [filteredAnalytics, analyticsCurrentPage]);

  // Calculate Device Percentages
  const deviceStats = useMemo(() => {
    const total = filteredAnalytics.length;
    if (total === 0) return { mobile: 0, desktop: 0 };
    const mobile = filteredAnalytics.filter(d => d.device === 'Mobile').length;
    const desktop = filteredAnalytics.filter(d => d.device === 'Desktop').length;
    return {
      mobile: Math.round((mobile / total) * 100),
      desktop: Math.round((desktop / total) * 100)
    };
  }, [filteredAnalytics]);

  // Calculate Source Percentages
  const sourceStats = useMemo(() => {
    const total = filteredAnalytics.length;
    if (total === 0) return { whatsapp: 0, direct: 0, other: 0 };
    const whatsapp = filteredAnalytics.filter(d => d.source === 'whatsapp').length;
    const direct = filteredAnalytics.filter(d => d.source === 'direct').length;
    const other = total - whatsapp - direct;
    return {
      whatsapp: Math.round((whatsapp / total) * 100),
      direct: Math.round((direct / total) * 100),
      other: Math.round((other / total) * 100)
    };
  }, [filteredAnalytics]);

  const topContent = useMemo(() => {
    if (!filteredAnalytics.length) return [];
    const counts = {};
    filteredAnalytics.forEach(view => {
      if (view.path && view.path.includes('?p=')) {
        const match = view.path.match(/\?p=([^&]+)/);
        if (match && match[1]) {
          counts[`post_${match[1]}`] = (counts[`post_${match[1]}`] || 0) + 1;
        }
      } else if (view.path && (view.path.includes('?quote=') || view.path.includes('/quote/'))) {
        const match = view.path.match(/(?:\?quote=|\/quote\/)([^&]+)/);
        if (match && match[1]) {
          counts[`quote_${match[1]}`] = (counts[`quote_${match[1]}`] || 0) + 1;
        }
      } else if (view.path && (view.path.includes('?poem=') || view.path.includes('/poem/'))) {
        const match = view.path.match(/(?:\?poem=|\/poem\/)([^&]+)/);
        if (match && match[1]) {
          counts[`poem_${match[1]}`] = (counts[`poem_${match[1]}`] || 0) + 1;
        }
      }
    });
    
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([key, count]) => {
        if (key.startsWith('post_')) {
          const id = key.replace('post_', '');
          const post = posts.find(p => p.id === id);
          return { id, type: 'post', title: post ? post.title : `Deleted Reflection (ID: ${id})`, count };
        } else if (key.startsWith('quote_')) {
          const id = key.replace('quote_', '');
          const quote = quotes.find(q => q.id === id);
          return { id, type: 'quote', title: quote ? `"${quote.text}"` : `Deleted Quote (ID: ${id})`, author: quote?.author, count };
        } else {
          const id = key.replace('poem_', '');
          const poem = poems.find(p => p.id === id);
          return { id, type: 'poem', title: poem ? poem.title : `Deleted Poem (ID: ${id})`, author: poem?.author, count };
        }
      });
  }, [filteredAnalytics, posts, quotes, poems]);

  const topLovedReflections = useMemo(() => {
    return [...posts].filter(p => p.likes > 0).sort((a, b) => b.likes - a.likes).slice(0, 5);
  }, [posts]);

  const topLovedQuotes = useMemo(() => {
    return [...quotes].filter(q => q.likes > 0).sort((a, b) => b.likes - a.likes).slice(0, 5);
  }, [quotes]);

  // eslint-disable-next-line no-unused-vars
  const topLovedPoems = useMemo(() => {
    return [...poems].filter(p => p.likes > 0).sort((a, b) => b.likes - a.likes).slice(0, 5);
  }, [poems]);

  const toggleSelection = (id) => {
    const newSet = new Set(selectedItems);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedItems(newSet);
  };

  const handleSelectAll = () => {
    let items = [];
    if (adminTab === 'blog') items = filteredPosts;
    else if (adminTab === 'quotes') items = paginatedQuotes;
    else if (adminTab === 'poems') items = paginatedPoems;
    else if (adminTab === 'diary') items = paginatedDiary;
    else if (adminTab === 'analytics') items = paginatedAnalytics; // only what's visible

    const allSelected = items.length > 0 && items.every(i => selectedItems.has(i.id));
    
    const newSet = new Set(selectedItems);
    if (allSelected) {
      items.forEach(i => newSet.delete(i.id));
    } else {
      items.forEach(i => newSet.add(i.id));
    }
    setSelectedItems(newSet);
  };

  const downloadPoemsCSV = () => {
    if (!poems || poems.length === 0) {
      showToast("No poems to export", "error");
      return;
    }

    const headers = ['Title', 'Text', 'Author', 'Date', 'Tags', 'Likes'];
    const csvRows = [headers.join(',')];

    poems.forEach(poem => {
      const escapeString = (str) => `"${String(str || '').replace(/"/g, '""')}"`;
      
      const title = poem.title || '';
      const text = poem.text || '';
      const author = poem.author || 'Ben Hasan';
      const date = poem.date || poem.year || '';
      const tags = Array.isArray(poem.tags) ? poem.tags.join(', ') : (poem.tags || '');
      const likes = poem.likes || 0;

      csvRows.push([escapeString(title), escapeString(text), escapeString(author), escapeString(date), escapeString(tags), escapeString(likes)].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `idiawisdom_poems_backup_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast(`Exported ${poems.length} poems!`, "success");
  };

  const handlePoemCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsSaving(true);
    showToast("Parsing Poem CSV...", "success");

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      try {
        const parsed = parseCSV(text);
        if (parsed.length < 2) throw new Error("CSV is empty or missing headers");
        
        const headers = parsed[0].map(h => h.toLowerCase().trim());
        const titleIdx = headers.findIndex(h => h === 'title' || h.includes('name') || h === 'poem title');
        const textIdx = headers.findIndex(h => h.includes('poem') || h.includes('text') || h.includes('verse') || h === 'content');
        const authorIdx = headers.findIndex(h => h.includes('author') || h.includes('by'));
        const dateIdx = headers.findIndex(h => h.includes('date') || h.includes('time'));
        const tagsIdx = headers.findIndex(h => h.includes('tag') || h.includes('category'));

        if (textIdx === -1) throw new Error("Could not find a Poem/Verse/Text column in CSV headers.");

        let successCount = 0;
        for (let i = 1; i < parsed.length; i++) {
          const row = parsed[i];
          if (!row || !row[textIdx] || !row[textIdx].trim()) continue;

          const pTitle = titleIdx !== -1 && row[titleIdx] ? row[titleIdx].trim() : '';
          const pText = row[textIdx].trim();
          const pAuthor = authorIdx !== -1 && row[authorIdx] ? row[authorIdx].trim() : 'Ben Hasan';
          const pDate = dateIdx !== -1 && row[dateIdx] ? row[dateIdx].trim() : new Date().toISOString().split('T')[0];
          const pTags = tagsIdx !== -1 && row[tagsIdx] ? row[tagsIdx].trim() : '';

          await submitPoem({ title: pTitle, text: pText, author: pAuthor, date: pDate, tags: pTags });
          successCount++;
        }
        showToast(`Successfully imported ${successCount} poems!`, "success");
      } catch (err) {
        console.error("Poem CSV Import Error:", err);
        showToast(err.message || "Failed to import Poem CSV", "error");
      } finally {
        setIsSaving(false);
        e.target.value = null;
      }
    };
    reader.readAsText(file);
  };

  const handleFindPoemDuplicates = () => {
    const textMap = new Map();
    const duplicates = new Set();

    poems.forEach(poem => {
      if (!poem.text) return;
      const normalizedText = poem.text.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      
      if (textMap.has(normalizedText)) {
        duplicates.add(poem.id);
      } else {
        textMap.set(normalizedText, poem.id);
      }
    });

    if (duplicates.size > 0) {
      setSelectedItems(duplicates);
      showToast(`Found ${duplicates.size} duplicate poem(s). Click Delete below.`, "success");
    } else {
      showToast("No duplicates found!", "success");
    }
  };

  const downloadDiaryCSV = () => {
    if (!diary || diary.length === 0) {
      showToast("No diary entries to export", "error");
      return;
    }

    const headers = ['Title', 'Author', 'Type', 'Date', 'Content', 'Rating', 'Link'];
    const csvRows = [headers.join(',')];

    diary.forEach(entry => {
      const escapeString = (str) => `"${String(str || '').replace(/"/g, '""')}"`;
      
      const title = entry.title || '';
      const author = entry.author || '';
      const type = entry.type || 'book';
      const date = entry.date || '';
      const content = entry.content || '';
      const rating = entry.rating || '';
      const link = entry.link || '';

      csvRows.push([
        escapeString(title), 
        escapeString(author), 
        escapeString(type), 
        escapeString(date), 
        escapeString(content), 
        escapeString(rating), 
        escapeString(link)
      ].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const linkElement = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    linkElement.setAttribute('href', url);
    linkElement.setAttribute('download', `idiawisdom_diary_backup_${new Date().toISOString().split('T')[0]}.csv`);
    linkElement.style.visibility = 'hidden';
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
    showToast(`Exported ${diary.length} diary entries!`, "success");
  };

  const handleDiaryCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsSaving(true);
    showToast("Parsing CSV...", "success");

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      try {
        const parsed = parseCSV(text);
        if (parsed.length < 2) throw new Error("CSV is empty or missing headers");
        
        const headers = parsed[0].map(h => h.toLowerCase().trim());
        const titleIdx = headers.findIndex(h => h.includes('title') || h.includes('name') || h === 'tajuk' || h === 'buku' || h === 'artikel');
        const authorIdx = headers.findIndex(h => h.includes('author') || h.includes('penulis') || h.includes('by'));
        const typeIdx = headers.findIndex(h => h.includes('type') || h.includes('kategori') || h.includes('jenis'));
        const dateIdx = headers.findIndex(h => h.includes('date') || h.includes('tarikh') || h.includes('time') || h === 'created');
        const contentIdx = headers.findIndex(h => h.includes('content') || h.includes('reflection') || h.includes('refleksi') || h.includes('catatan') || h === 'text' || h === 'body');
        const ratingIdx = headers.findIndex(h => h.includes('rating') || h.includes('penarafan') || h.includes('star') || h === 'rate');
        const linkIdx = headers.findIndex(h => h.includes('link') || h.includes('pautan') || h === 'url');

        if (titleIdx === -1) throw new Error("Could not find a Title column in CSV headers.");
        if (contentIdx === -1) throw new Error("Could not find a Content/Reflection column in CSV headers.");

        let successCount = 0;
        for (let i = 1; i < parsed.length; i++) {
          const row = parsed[i];
          if (!row || !row[titleIdx] || !row[titleIdx].trim() || !row[contentIdx] || !row[contentIdx].trim()) continue;

          const titleVal = row[titleIdx].trim();
          const authorVal = authorIdx !== -1 && row[authorIdx] ? row[authorIdx].trim() : '';
          
          let typeVal = 'book';
          if (typeIdx !== -1 && row[typeIdx]) {
            const rawType = row[typeIdx].trim().toLowerCase();
            if (rawType.includes('article') || rawType.includes('artikel')) {
              typeVal = 'article';
            }
          }
          
          const dateVal = dateIdx !== -1 && row[dateIdx] ? row[dateIdx].trim() : new Date().toISOString().split('T')[0];
          const contentVal = row[contentIdx].trim();
          
          let ratingVal = null;
          if (ratingIdx !== -1 && row[ratingIdx]) {
            const parsedRating = parseInt(row[ratingIdx].trim(), 10);
            if (!isNaN(parsedRating) && parsedRating >= 1 && parsedRating <= 5) {
              ratingVal = parsedRating;
            }
          }
          
          const linkVal = linkIdx !== -1 && row[linkIdx] ? row[linkIdx].trim() : '';

          await submitDiary({
            title: titleVal,
            author: authorVal,
            type: typeVal,
            date: dateVal,
            content: contentVal,
            rating: ratingVal,
            link: linkVal
          });
          successCount++;
        }
        showToast(`Successfully imported ${successCount} reflections!`, "success");
      } catch (err) {
        console.error("Diary CSV Import Error:", err);
        showToast(err.message || "Failed to import CSV", "error");
      } finally {
        setIsSaving(false);
        e.target.value = null;
      }
    };
    reader.readAsText(file);
  };

  const downloadCSV = () => {
    if (filteredAnalytics.length === 0) {
      showToast("No data to download", "error");
      return;
    }

    const headers = ['Date', 'Path', 'City', 'Country', 'Device', 'Source', 'Role'];
    const csvRows = [headers.join(',')];

    filteredAnalytics.forEach(row => {
      const date = row.timestamp?.toDate ? row.timestamp.toDate().toLocaleString() : 'Unknown';
      const path = row.path || '';
      const city = row.city && row.city !== 'Unknown' && row.city !== 'undefined' ? row.city : '';
      const country = row.country && row.country !== 'Unknown' && row.country !== 'undefined' ? row.country : '';
      const device = row.device || '';
      const source = row.source || '';
      const role = row.role || 'user';

      // Escape quotes and wrap in quotes to safely handle commas within values
      const escapeString = (str) => `"${String(str).replace(/"/g, '""')}"`;

      csvRows.push([
        escapeString(date), escapeString(path), escapeString(city), escapeString(country),
        escapeString(device), escapeString(source), escapeString(role)
      ].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `idiawisdom_analytics_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadQuotesCSV = () => {
    if (!quotes || quotes.length === 0) {
      showToast("No quotes to export", "error");
      return;
    }

    const headers = ['Text', 'Author', 'Date', 'Tags', 'Likes'];
    const csvRows = [headers.join(',')];

    quotes.forEach(quote => {
      const escapeString = (str) => `"${String(str || '').replace(/"/g, '""')}"`;
      
      const text = quote.text || '';
      const author = quote.author || 'Ben Hasan';
      const date = quote.date || quote.year || '';
      const tags = Array.isArray(quote.tags) ? quote.tags.join(', ') : (quote.tags || '');
      const likes = quote.likes || 0;

      csvRows.push([escapeString(text), escapeString(author), escapeString(date), escapeString(tags), escapeString(likes)].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `idiawisdom_quotes_backup_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast(`Exported ${quotes.length} quotes!`, "success");
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsSaving(true);
    showToast("Parsing CSV...", "success");

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      try {
        const parsed = parseCSV(text);
        if (parsed.length < 2) throw new Error("CSV is empty or missing headers");
        
        const headers = parsed[0].map(h => h.toLowerCase().trim());
        // Auto-detect columns based on standard Notion export headers
        const textIdx = headers.findIndex(h => h.includes('quote') || h.includes('text') || h.includes('name') || h === 'title');
        const authorIdx = headers.findIndex(h => h.includes('author') || h.includes('by'));
        const dateIdx = headers.findIndex(h => h.includes('date') || h.includes('time'));
        const tagsIdx = headers.findIndex(h => h.includes('tag') || h.includes('category'));

        if (textIdx === -1) throw new Error("Could not find a Quote/Text column in CSV headers.");

        let successCount = 0;
        // Loop sequentially so we don't overwhelm Firestore with concurrent requests
        for (let i = 1; i < parsed.length; i++) {
          const row = parsed[i];
          if (!row || !row[textIdx] || !row[textIdx].trim()) continue; // Skip empty rows

          const qText = row[textIdx].trim();
          const qAuthor = authorIdx !== -1 && row[authorIdx] ? row[authorIdx].trim() : 'Ben Hasan';
          const qDate = dateIdx !== -1 && row[dateIdx] ? row[dateIdx].trim() : new Date().toISOString().split('T')[0];
          const qTags = tagsIdx !== -1 && row[tagsIdx] ? row[tagsIdx].trim() : '';

          await submitQuote({ text: qText, author: qAuthor, date: qDate, tags: qTags });
          successCount++;
        }
        showToast(`Successfully imported ${successCount} quotes!`, "success");
      } catch (err) {
        console.error("CSV Import Error:", err);
        showToast(err.message || "Failed to import CSV", "error");
      } finally {
        setIsSaving(false);
        e.target.value = null; // Reset input so you can upload the same file again if needed
      }
    };
    reader.readAsText(file);
  };

  const handleFindDuplicates = () => {
    const textMap = new Map();
    const duplicates = new Set();

    quotes.forEach(quote => {
      if (!quote.text) return;
      // Normalize text by removing all non-alphanumeric characters and lowering case
      // This ensures "Hello, world!" and "hello world" are correctly treated as duplicates
      const normalizedText = quote.text.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      
      if (textMap.has(normalizedText)) {
        duplicates.add(quote.id);
      } else {
        textMap.set(normalizedText, quote.id);
      }
    });

    if (duplicates.size > 0) {
      setSelectedItems(duplicates);
      showToast(`Found ${duplicates.size} duplicate(s). Click Delete below.`, "success");
    } else {
      showToast("No duplicates found!", "success");
    }
  };

  const categoryCounts = useMemo(() => {
    const counts = { All: 0 };
    if (categories) {
      categories.forEach(cat => (counts[cat] = 0));
    }

    const relevantPosts = posts.filter(p => statusFilter === 'all' || p.status === statusFilter);
    
    counts.All = relevantPosts.length;
    relevantPosts.forEach(p => {
      if (p.category && counts[p.category] !== undefined) {
        counts[p.category]++;
      }
    });

    return counts;
  }, [posts, categories, statusFilter]);

  // Quote Extraction Logic
  const { quoteAvailableYears, quoteAvailableMonths, quoteYearCounts, quoteMonthCounts } = useMemo(() => {
    const yearCounts = {};
    const monthCounts = {};
    let totalYrs = 0;
    let totalMths = 0;
    
    quotes.forEach(q => {
      const dateObj = safeParseDate(q.date);
      const qYear = dateObj ? dateObj.getFullYear().toString() : (q.year?.toString() || '');
      const qMonth = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
      
      const matchY = quoteSelectedYear === 'All' || qYear === quoteSelectedYear;
      const matchM = quoteSelectedMonth === 'All' || qMonth === quoteSelectedMonth;

      if (matchM && qYear) {
        yearCounts[qYear] = (yearCounts[qYear] || 0) + 1;
        totalYrs++;
      }
      if (matchY && qMonth) {
        monthCounts[qMonth] = (monthCounts[qMonth] || 0) + 1;
        totalMths++;
      }
    });
    
    yearCounts['All'] = totalYrs;
    monthCounts['All'] = totalMths;

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const sortedMonths = Object.keys(monthCounts).filter(k => k !== 'All').sort((a, b) => monthNames.indexOf(a) - monthNames.indexOf(b));
    
    return {
      quoteAvailableYears: Object.keys(yearCounts).filter(k => k !== 'All').sort((a, b) => b - a),
      quoteAvailableMonths: sortedMonths,
      quoteYearCounts: yearCounts,
      quoteMonthCounts: monthCounts
    };
  }, [quotes, quoteSelectedYear, quoteSelectedMonth]);

  const quoteDisplayYears = quoteSelectedYear !== 'All' && !quoteAvailableYears.includes(quoteSelectedYear) ? [quoteSelectedYear, ...quoteAvailableYears] : quoteAvailableYears;
  const quoteDisplayMonths = quoteSelectedMonth !== 'All' && !quoteAvailableMonths.includes(quoteSelectedMonth) ? [quoteSelectedMonth, ...quoteAvailableMonths] : quoteAvailableMonths;

  const filteredQuotes = useMemo(() => {
    let result = quotes;
    if (quoteSearchQuery.trim()) {
      const lowerQuery = quoteSearchQuery.toLowerCase();
      result = result.filter(q => (q.text && q.text.toLowerCase().includes(lowerQuery)) || (q.author && q.author.toLowerCase().includes(lowerQuery)) || (q.tags && (Array.isArray(q.tags) ? q.tags.join(' ') : q.tags).toLowerCase().includes(lowerQuery)));
    }
    if (quoteSelectedYear !== 'All' || quoteSelectedMonth !== 'All') {
      result = result.filter(q => {
        const dateObj = safeParseDate(q.date);
        const year = dateObj ? dateObj.getFullYear().toString() : (q.year?.toString() || '');
        const month = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
        const matchYear = quoteSelectedYear === 'All' || year === quoteSelectedYear;
        const matchMonth = quoteSelectedMonth === 'All' || month === quoteSelectedMonth;
        return matchYear && matchMonth;
      });
    }
    return result;
  }, [quotes, quoteSelectedYear, quoteSelectedMonth, quoteSearchQuery]);

  const quoteTotalPages = Math.ceil(filteredQuotes.length / itemsPerPage);
  const paginatedQuotes = filteredQuotes.slice((quoteCurrentPage - 1) * itemsPerPage, quoteCurrentPage * itemsPerPage);

  const hasQuoteActiveFilters = quoteSearchQuery !== '' || quoteSelectedYear !== 'All' || quoteSelectedMonth !== 'All';
  const handleResetQuoteFilters = () => {
    setQuoteSearchQuery('');
    setQuoteSelectedYear('All');
    setQuoteSelectedMonth('All');
  };

  // Poem Extraction & Filtering Logic
  const { poemAvailableYears, poemAvailableMonths, poemYearCounts, poemMonthCounts } = useMemo(() => {
    const yearCounts = {};
    const monthCounts = {};
    let totalYrs = 0;
    let totalMths = 0;
    
    poems.forEach(p => {
      const dateObj = safeParseDate(p.date);
      const pYear = dateObj ? dateObj.getFullYear().toString() : (p.year?.toString() || '');
      const pMonth = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
      
      const matchY = poemSelectedYear === 'All' || pYear === poemSelectedYear;
      const matchM = poemSelectedMonth === 'All' || pMonth === poemSelectedMonth;

      if (matchM && pYear) {
        yearCounts[pYear] = (yearCounts[pYear] || 0) + 1;
        totalYrs++;
      }
      if (matchY && pMonth) {
        monthCounts[pMonth] = (monthCounts[pMonth] || 0) + 1;
        totalMths++;
      }
    });
    
    yearCounts['All'] = totalYrs;
    monthCounts['All'] = totalMths;

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const sortedMonths = Object.keys(monthCounts).filter(k => k !== 'All').sort((a, b) => monthNames.indexOf(a) - monthNames.indexOf(b));
    
    return {
      poemAvailableYears: Object.keys(yearCounts).filter(k => k !== 'All').sort((a, b) => b - a),
      poemAvailableMonths: sortedMonths,
      poemYearCounts: yearCounts,
      poemMonthCounts: monthCounts
    };
  }, [poems, poemSelectedYear, poemSelectedMonth]);

  const poemDisplayYears = poemSelectedYear !== 'All' && !poemAvailableYears.includes(poemSelectedYear) ? [poemSelectedYear, ...poemAvailableYears] : poemAvailableYears;
  const poemDisplayMonths = poemSelectedMonth !== 'All' && !poemAvailableMonths.includes(poemSelectedMonth) ? [poemSelectedMonth, ...poemAvailableMonths] : poemAvailableMonths;

  const filteredPoems = useMemo(() => {
    let result = poems;
    if (poemSearchQuery.trim()) {
      const lowerQuery = poemSearchQuery.toLowerCase();
      result = result.filter(p => 
        (p.text && p.text.toLowerCase().includes(lowerQuery)) || 
        (p.title && p.title.toLowerCase().includes(lowerQuery)) || 
        (p.author && p.author.toLowerCase().includes(lowerQuery)) || 
        (p.tags && (Array.isArray(p.tags) ? p.tags.join(' ') : p.tags).toLowerCase().includes(lowerQuery))
      );
    }
    if (poemSelectedYear !== 'All' || poemSelectedMonth !== 'All') {
      result = result.filter(p => {
        const dateObj = safeParseDate(p.date);
        const year = dateObj ? dateObj.getFullYear().toString() : (p.year?.toString() || '');
        const month = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
        const matchYear = poemSelectedYear === 'All' || year === poemSelectedYear;
        const matchMonth = poemSelectedMonth === 'All' || month === poemSelectedMonth;
        return matchYear && matchMonth;
      });
    }
    return result;
  }, [poems, poemSelectedYear, poemSelectedMonth, poemSearchQuery]);

  const poemTotalPages = Math.ceil(filteredPoems.length / itemsPerPage);
  const paginatedPoems = filteredPoems.slice((poemCurrentPage - 1) * itemsPerPage, poemCurrentPage * itemsPerPage);

  const hasPoemActiveFilters = poemSearchQuery !== '' || poemSelectedYear !== 'All' || poemSelectedMonth !== 'All';
  const handleResetPoemFilters = () => {
    setPoemSearchQuery('');
    setPoemSelectedYear('All');
    setPoemSelectedMonth('All');
  };

  // Diary Filtering & Extraction Logic
  const { diaryAvailableYears, diaryAvailableMonths, diaryYearCounts, diaryMonthCounts } = useMemo(() => {
    const yearCounts = {};
    const monthCounts = {};
    let totalYrs = 0;
    let totalMths = 0;
    
    (diary || []).forEach(d => {
      const dateObj = safeParseDate(d.date);
      const dYear = dateObj ? dateObj.getFullYear().toString() : '';
      const dMonth = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
      
      const matchY = diarySelectedYear === 'All' || dYear === diarySelectedYear;
      const matchM = diarySelectedMonth === 'All' || dMonth === diarySelectedMonth;

      if (matchM && dYear) {
        yearCounts[dYear] = (yearCounts[dYear] || 0) + 1;
        totalYrs++;
      }
      if (matchY && dMonth) {
        monthCounts[dMonth] = (monthCounts[dMonth] || 0) + 1;
        totalMths++;
      }
    });
    
    yearCounts['All'] = totalYrs;
    monthCounts['All'] = totalMths;

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const sortedMonths = Object.keys(monthCounts).filter(k => k !== 'All').sort((a, b) => monthNames.indexOf(a) - monthNames.indexOf(b));
    
    return {
      diaryAvailableYears: Object.keys(yearCounts).filter(k => k !== 'All').sort((a, b) => b - a),
      diaryAvailableMonths: sortedMonths,
      diaryYearCounts: yearCounts,
      diaryMonthCounts: monthCounts
    };
  }, [diary, diarySelectedYear, diarySelectedMonth]);

  const diaryDisplayYears = diarySelectedYear !== 'All' && !diaryAvailableYears.includes(diarySelectedYear) ? [diarySelectedYear, ...diaryAvailableYears] : diaryAvailableYears;
  const diaryDisplayMonths = diarySelectedMonth !== 'All' && !diaryAvailableMonths.includes(diarySelectedMonth) ? [diarySelectedMonth, ...diaryAvailableMonths] : diaryAvailableMonths;

  const filteredDiary = useMemo(() => {
    let result = diary || [];
    if (diarySearchQuery.trim()) {
      const lowerQuery = diarySearchQuery.toLowerCase();
      result = result.filter(d => 
        (d.title && d.title.toLowerCase().includes(lowerQuery)) || 
        (d.content && d.content.toLowerCase().includes(lowerQuery)) || 
        (d.author && d.author.toLowerCase().includes(lowerQuery))
      );
    }
    if (diaryStatusFilter !== 'all') {
      result = result.filter(d => (d.status || 'published') === diaryStatusFilter);
    }
    if (diarySelectedYear !== 'All' || diarySelectedMonth !== 'All') {
      result = result.filter(d => {
        const dateObj = safeParseDate(d.date);
        const year = dateObj ? dateObj.getFullYear().toString() : '';
        const month = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
        const matchYear = diarySelectedYear === 'All' || year === diarySelectedYear;
        const matchMonth = diarySelectedMonth === 'All' || month === diarySelectedMonth;
        return matchYear && matchMonth;
      });
    }
    return result;
  }, [diary, diarySelectedYear, diarySelectedMonth, diarySearchQuery, diaryStatusFilter]);

  const paginatedDiary = filteredDiary.slice((diaryCurrentPage - 1) * itemsPerPage, diaryCurrentPage * itemsPerPage);

  // Group filtered diary entries by book for the grouped view
  const diaryBooks = useMemo(() => {
    const map = new Map();
    filteredDiary.forEach(entry => {
      const key = (entry.title || '').toLowerCase().trim() + '|||' + (entry.author || '').toLowerCase().trim();
      if (!map.has(key)) {
        map.set(key, {
          key,
          title: entry.title || 'Untitled',
          author: entry.author || 'Unknown',
          type: entry.type || 'book',
          entries: [],
          topics: new Set()
        });
      }
      const book = map.get(key);
      book.entries.push(entry);
      if (entry.topic?.trim()) book.topics.add(entry.topic.trim());
    });
    return Array.from(map.values()).map(b => ({ ...b, topicCount: b.topics.size })).sort((a, b) => b.entries.length - a.entries.length);
  }, [filteredDiary]);

  const hasDiaryActiveFilters = diarySearchQuery !== '' || diarySelectedYear !== 'All' || diarySelectedMonth !== 'All';
  const handleResetDiaryFilters = () => {
    setDiarySearchQuery('');
    setDiarySelectedYear('All');
    setDiarySelectedMonth('All');
  };

  useEffect(() => {
    setDiaryCurrentPage(1);
  }, [diarySearchQuery, diarySelectedYear, diarySelectedMonth]);

  // Filter posts for Admin View
  const filteredPosts = posts.filter(p => 
    (statusFilter === 'all' || p.status === statusFilter) &&
    (categoryFilter === 'All' || p.category === categoryFilter) &&
    (p.title?.toLowerCase().includes(searchQuery.toLowerCase()) || false)
  ).sort((a, b) => {
    if (sortBy === 'date-desc') return new Date(b.date || 0) - new Date(a.date || 0);
    if (sortBy === 'date-asc') return new Date(a.date || 0) - new Date(b.date || 0);
    if (sortBy === 'title-asc') return (a.title || '').localeCompare(b.title || '');
    if (sortBy === 'title-desc') return (b.title || '').localeCompare(a.title || '');
    return 0;
  });

  // Pagination Logic
  const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Design tokens
  const pillStyle = { borderRadius: '9999px' };
  const cardStyle = { borderRadius: '32px' };

  const showToast = (message, type = 'success') => setToast({ message, type });

  const handleToolbarAction = (e, cmd) => {
    e.preventDefault();
    e.stopPropagation();
    executeCommand(cmd);
  };

  if (!isAdminLoggedIn) {
    return (
      <section className="pt-40 pb-24 px-4 min-h-screen flex items-center justify-center animate-in fade-in duration-1000">
        <div className="max-w-md w-full text-center space-y-8">
          <div className="space-y-4">
            <div className={`w-20 h-20 mx-auto rounded-full border flex items-center justify-center mb-8 ${isDarkMode ? 'border-stone-800' : 'border-stone-100 shadow-xl'}`}>
              <Fingerprint size={32} className={isDarkMode ? 'text-stone-700' : 'text-stone-300'} />
            </div>
            <h2 className={`text-4xl font-serif ${theme.text}`}>Restricted Space</h2>
            <p className={`text-sm opacity-50 leading-relaxed ${theme.textMuted}`}>This area is reserved for the author's reflections.</p>
          </div>
          <button onClick={handleGoogleLogin} style={pillStyle} className="w-auto mx-auto px-10 py-4 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-[0.2em] shadow-2xl hover:opacity-90 transition-all flex items-center justify-center gap-3">
            Authenticate with Google
          </button>
          {loginError && <p className="text-red-500 text-[10px] font-bold uppercase tracking-widest animate-pulse">{loginError}</p>}
        </div>
      </section>
    );
  }

  if (isComposing) {
    const syncUI = (() => {
      switch(saveStatus) {
        case 'saving': return { label: 'Syncing', icon: <Loader2 size={12} className="animate-spin" />, classes: 'bg-amber-500/10 text-amber-500 border-amber-500/10' };
        case 'success': return { label: 'Saved', icon: <CheckCircle2 size={12} />, classes: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/10' };
        default: return { label: 'Drafting', icon: <PenTool size={12} />, classes: isDarkMode ? 'bg-stone-800 text-stone-500 border-stone-700' : 'bg-stone-100 text-stone-400 border-stone-200' };
      }
    })();

    return (
      <section className="pt-32 pb-24 px-4 md:px-6 min-h-screen animate-in fade-in duration-700">
        <div className="max-w-4xl mx-auto space-y-12">
          <div className={`flex flex-col md:flex-row md:items-center justify-between border-b pb-8 gap-6 ${theme.border}`}>
            <div className="flex items-center gap-6">
              <h2 className={`text-4xl font-serif ${theme.text}`}>{editingPostId ? 'Edit Reflection' : 'New Reflection'}</h2>
              <div style={pillStyle} className={`flex items-center gap-2 px-4 py-1.5 border text-[10px] font-bold uppercase tracking-widest ${syncUI.classes}`}>
                {syncUI.icon} <span>{syncUI.label}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowCancelModal(true)} className={`px-6 py-2.5 text-xs font-bold uppercase tracking-widest ${theme.textMuted} hover:${theme.text}`}>Cancel</button>
              <button onClick={() => handleSavePost('draft')} style={pillStyle} className={`px-6 py-2.5 border text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-100 text-stone-500 hover:bg-stone-50'}`}>Save Draft</button>
              <button onClick={() => setShowPublishModal(true)} style={pillStyle} className="px-8 py-2.5 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-xs font-bold uppercase tracking-widest shadow-xl">Publish</button>
            </div>
          </div>

          {localDraftAvailable && (
            <div className="p-5 rounded-2xl border flex flex-col md:flex-row md:items-center justify-between gap-4 animate-in slide-in-from-top-4 duration-500 bg-amber-500/5 border-amber-500/20">
              <div className="flex items-center gap-3">
                <AlertCircle className="text-amber-500 flex-shrink-0" size={18} />
                <div className="space-y-0.5 text-left">
                  <h4 className={`text-xs font-bold uppercase tracking-wider ${theme.text}`}>Unsaved Local Draft Detected</h4>
                  <p className={`text-[10px] opacity-60 ${theme.textMuted}`}>We found a newer draft of this reflection cached in your local browser history.</p>
                </div>
              </div>
              <div className="flex items-center gap-3 self-end md:self-center">
                <button onClick={clearLocalDraft} className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity">Discard</button>
                <button onClick={restoreLocalDraft} style={pillStyle} className="px-5 py-2 bg-amber-500 text-stone-950 text-[10px] font-bold uppercase tracking-widest hover:opacity-90 transition-opacity shadow-sm">Restore Progress</button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-8">
            <div className="flex items-center gap-3">
              <select value={composeCategory} onChange={(e) => { setComposeCategory(e.target.value); triggerSmartSync(); }} style={pillStyle} className={`px-6 py-2.5 border text-[10px] font-bold uppercase tracking-widest outline-none appearance-none cursor-pointer ${isDarkMode ? 'bg-stone-900 border-stone-800 text-stone-400' : 'bg-stone-50 border-stone-100 text-stone-500'}`}>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <button onClick={() => setShowCategoryManager(true)} style={pillStyle} className={`p-2.5 border ${theme.border} ${theme.textMuted} hover:${theme.text}`}><Settings size={14} /></button>
            </div>
            
            <div style={pillStyle} className={`flex items-center gap-3 px-4 py-2.5 border ${isDarkMode ? 'bg-stone-900 border-stone-800 text-stone-400' : 'bg-stone-50 border-stone-100 text-stone-500'}`}>
              <Calendar size={14} />
              <input 
                type="date" 
                value={composeDate} 
                onChange={(e) => { setComposeDate(e.target.value); triggerSmartSync(); }}
                className="bg-transparent outline-none text-[10px] font-bold uppercase tracking-widest"
              />
            </div>

            <div className="flex items-center gap-6 text-[10px] font-bold uppercase tracking-widest opacity-30">
              <span className="flex items-center gap-2"><Hash size={12}/> {liveStats?.words || 0} Words</span>
              <span className="flex items-center gap-2"><Clock size={12}/> {liveStats?.time || 0} Min Read</span>
            </div>
          </div>

          <input type="text" placeholder="The Title..." value={composeTitle} onChange={e => { setComposeTitle(e.target.value); triggerSmartSync(); }} className={`w-full text-3xl md:text-5xl font-serif outline-none bg-transparent placeholder:opacity-10 ${theme.text}`} />

          <div style={{ ...pillStyle, zIndex: 100 }} className={`sticky top-24 flex overflow-x-auto no-scrollbar items-center gap-1 p-1.5 border shadow-2xl backdrop-blur-xl self-start max-w-full w-full md:w-auto [&>*]:flex-shrink-0 ${isDarkMode ? 'bg-stone-900/80 border-stone-800' : 'bg-white/80 border-stone-100'}`}>
            <button onMouseDown={(e) => handleToolbarAction(e, 'bold')} className={`p-2.5 rounded-full ${editor?.isActive('bold') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Bold size={16} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'italic')} className={`p-2.5 rounded-full ${editor?.isActive('italic') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Italic size={16} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'underline')} className={`p-2.5 rounded-full ${editor?.isActive('underline') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Underline size={16} /></button>
            
            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />

            <button onMouseDown={(e) => handleToolbarAction(e, 'h1')} className={`px-3 py-1.5 rounded-full text-[10px] font-bold ${editor?.isActive('heading', { level: 1 }) ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}>H1</button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'h2')} className={`px-3 py-1.5 rounded-full text-[10px] font-bold ${editor?.isActive('heading', { level: 2 }) ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}>H2</button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'h3')} className={`px-3 py-1.5 rounded-full text-[10px] font-bold ${editor?.isActive('heading', { level: 3 }) ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}>H3</button>

            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />

            <button onMouseDown={(e) => handleToolbarAction(e, 'bullet')} className={`p-2.5 rounded-full ${editor?.isActive('bulletList') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><List size={16} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'number')} className={`p-2.5 rounded-full ${editor?.isActive('orderedList') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><ListOrdered size={16} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'blockquote')} className={`p-2.5 rounded-full ${editor?.isActive('blockquote') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Quote size={16} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'codeBlock')} className={`p-2.5 rounded-full ${editor?.isActive('codeBlock') ? 'bg-stone-500/20 text-stone-900 dark:text-white' : 'text-stone-500 hover:bg-stone-500/10'}`}><Code size={16} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'divider')} className="p-2.5 text-stone-500 hover:bg-stone-500/10 rounded-full"><Minus size={16} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'emDash')} className="px-3 py-1 text-stone-500 hover:bg-stone-500/10 rounded-full font-serif font-bold" title="Insert Em Dash">—</button>

            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />

            <button onClick={handleGrammarCheck} disabled={isCheckingGrammar}
              className={`p-2 rounded-full transition-all ${
                grammarIssues.length > 0
                  ? 'text-emerald-500 bg-emerald-500/10'
                  : isCheckingGrammar
                    ? 'text-amber-500 animate-pulse'
                    : 'text-stone-500 hover:bg-stone-500/10'
              }`}
              title={isCheckingGrammar ? 'Checking...' : 'Check Grammar'}>
              {isCheckingGrammar ? <Loader2 size={16} className="animate-spin" /> : <SpellCheck size={16} />}
            </button>

            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />

            <button onMouseDown={(e) => handleToolbarAction(e, 'undo')} className="p-2.5 text-stone-500 hover:bg-stone-500/10 rounded-full"><Undo size={16} /></button>
            <button onMouseDown={(e) => handleToolbarAction(e, 'redo')} className="p-2.5 text-stone-500 hover:bg-stone-500/10 rounded-full"><Redo size={16} /></button>

            <div className="w-px h-4 bg-stone-500 mx-1 opacity-20" />
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setEditorFontSize(prev => Math.max(1, prev - 0.1))} className="p-2.5 text-stone-500 hover:bg-stone-500/10 rounded-full" title="Decrease Editor Font Size"><Minus size={16} /></button>
            <span className="flex items-center justify-center text-[10px] font-bold text-stone-500 w-10 text-center gap-1" title="Editor Font Size"><Type size={12} /> {Math.round(editorFontSize * 16)}</span>
            <button onMouseDown={(e) => e.preventDefault()} onClick={() => setEditorFontSize(prev => Math.min(4, prev + 0.1))} className="p-2.5 text-stone-500 hover:bg-stone-500/10 rounded-full" title="Increase Editor Font Size"><Plus size={16} /></button>
          </div>

          <style>{`
            .editor-dynamic-font .ProseMirror,
            .editor-dynamic-font .ProseMirror p,
            .editor-dynamic-font .ProseMirror ul,
            .editor-dynamic-font .ProseMirror ol {
              font-size: ${editorFontSize}rem !important;
            }
            .editor-dynamic-font .ProseMirror h1 { font-size: ${editorFontSize * 2}rem !important; }
            .editor-dynamic-font .ProseMirror h2 { font-size: ${editorFontSize * 1.5}rem !important; }
            .editor-dynamic-font .ProseMirror h3 { font-size: ${editorFontSize * 1.25}rem !important; }

            ${isDarkMode ? `
            /* Force absolute white colors for high contrast in dark mode */
            .editor-dynamic-font {
              --tw-prose-body: #ffffff !important;
              --tw-prose-headings: #ffffff !important;
              --tw-prose-bold: #ffffff !important;
              --tw-prose-quotes: #e7e5e4 !important;
              --tw-prose-counters: #ffffff !important;
              --tw-prose-bullets: #ffffff !important;
            }
            .editor-dynamic-font .ProseMirror li::marker {
              color: #ffffff !important;
            }
            ` : `
            /* Force black numbering/bullets in light mode */
            .editor-dynamic-font {
              --tw-prose-counters: #000000 !important;
              --tw-prose-bullets: #000000 !important;
            }
            .editor-dynamic-font .ProseMirror li::marker {
              color: #000000 !important;
            }
            `}
            /* Enforce readable colors in both modes by overriding inline paste styles */
            .editor-dynamic-font .ProseMirror * { background-color: transparent !important; }
            .editor-dynamic-font .ProseMirror pre { background-color: var(--tw-prose-pre-bg) !important; }
            .editor-dynamic-font .ProseMirror p, 
            .editor-dynamic-font .ProseMirror span, 
            .editor-dynamic-font .ProseMirror div, 
            .editor-dynamic-font .ProseMirror li { color: var(--tw-prose-body) !important; }
            .editor-dynamic-font .ProseMirror h1, .editor-dynamic-font .ProseMirror h2, 
            .editor-dynamic-font .ProseMirror h3, .editor-dynamic-font .ProseMirror h4, 
            .editor-dynamic-font .ProseMirror h5, .editor-dynamic-font .ProseMirror h6 { color: var(--tw-prose-headings) !important; }
            .editor-dynamic-font .ProseMirror a { color: var(--tw-prose-links) !important; }
            .editor-dynamic-font .ProseMirror strong, .editor-dynamic-font .ProseMirror b { color: var(--tw-prose-bold) !important; }
            .editor-dynamic-font .ProseMirror blockquote { color: var(--tw-prose-quotes) !important; }
            .editor-dynamic-font .ProseMirror code { color: var(--tw-prose-code) !important; }
            .editor-dynamic-font .ProseMirror pre code { color: var(--tw-prose-pre-code) !important; background-color: transparent !important; }
          `}</style>

          <div className={`editor-dynamic-font prose prose-stone dark:prose-invert max-w-none leading-loose font-garamond min-h-[60vh] pb-40 ${theme.text}`}
               style={{ fontSize: `${editorFontSize}rem` }}>
            <EditorContent editor={editor} />
          </div>

          {/* Grammar Check Panel */}
          {(grammarIssues.length > 0 || isCheckingGrammar) && (
            <div className="mb-8">
              <GrammarCheckPanel
                issues={grammarIssues}
                isChecking={isCheckingGrammar}
                onReplace={replaceGrammarFix}
                onIgnore={ignoreGrammarIssue}
                onClose={clearGrammarIssues}
                isDarkMode={isDarkMode}
                theme={theme}
              />
            </div>
          )}

          {/* Category Manager Modal */}
          {showCategoryManager && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowCategoryManager(false)} />
              <div style={cardStyle} className={`relative w-full max-w-md p-8 shadow-2xl border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                <div className="flex items-center justify-between mb-8">
                  <h3 className={`text-xl font-serif ${theme.text}`}>Manage Categories</h3>
                  <button onClick={() => setShowCategoryManager(false)} className={`p-2 hover:bg-stone-500/10 rounded-full transition-colors ${theme.text}`}><X size={18} /></button>
                </div>
                
                <div className="space-y-6">
                  <div className="flex gap-3">
                    <input 
                      type="text" 
                      value={newCategoryInput}
                      onChange={(e) => setNewCategoryInput(e.target.value)}
                      placeholder="New Category..."
                      className={`flex-1 px-6 py-3 border rounded-full text-xs font-bold uppercase tracking-widest outline-none ${isDarkMode ? 'bg-stone-800 border-stone-700 text-white placeholder:text-stone-600' : 'bg-stone-50 border-stone-200 text-stone-900 placeholder:text-stone-400'}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newCategoryInput.trim()) {
                          if (!categories.includes(newCategoryInput.trim())) {
                              updateCategories([...categories, newCategoryInput.trim()]);
                              setNewCategoryInput('');
                          }
                        }
                      }}
                    />
                    <button 
                      onClick={() => {
                        if (newCategoryInput.trim() && !categories.includes(newCategoryInput.trim())) {
                          updateCategories([...categories, newCategoryInput.trim()]);
                          setNewCategoryInput('');
                        }
                      }}
                      style={pillStyle}
                      className={`px-6 py-3 border text-[10px] font-bold uppercase tracking-widest hover:opacity-80 transition-opacity ${isDarkMode ? 'bg-stone-100 text-stone-900' : 'bg-stone-900 text-white'}`}
                    >
                      Add
                    </button>
                  </div>

                  <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2">
                    {categories.map((cat, idx) => (
                      <div key={idx} className={`flex items-center justify-between px-6 py-4 rounded-full border group ${isDarkMode ? 'border-stone-800 bg-stone-800/30' : 'border-stone-100 bg-stone-50/50'}`}>
                        <span className={`text-xs font-bold uppercase tracking-widest ${theme.text}`}>{cat}</span>
                        <button 
                          onClick={() => {
                              const newCats = categories.filter((_, i) => i !== idx);
                              updateCategories(newCats);
                              if (composeCategory === cat && newCats.length > 0) {
                                  setComposeCategory(newCats[0]);
                              }
                          }}
                          className="text-red-500 opacity-20 group-hover:opacity-100 transition-opacity p-1"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Publish Confirmation Modal */}
          {showPublishModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowPublishModal(false)} />
              <div style={cardStyle} className={`relative w-full max-w-sm p-10 shadow-2xl border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                <div className="text-center space-y-6">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                    <CheckCircle2 size={32} className="text-emerald-500" />
                  </div>
                  <div className="space-y-2">
                    <h3 className={`text-2xl font-serif font-bold ${theme.text}`}>Ready to Publish?</h3>
                    <p className={`text-sm opacity-60 ${theme.textMuted}`}>This will make your reflection visible to the world.</p>
                  </div>
                  <div className="flex flex-col gap-3 pt-4">
                    <button 
                      onClick={() => {
                        handleSavePost('published');
                        setShowPublishModal(false);
                      }}
                      style={pillStyle}
                      className="w-full py-4 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-[0.2em] hover:opacity-90 transition-all shadow-xl"
                    >
                      Publish Now
                    </button>
                    <button 
                      onClick={() => setShowPublishModal(false)}
                      style={pillStyle}
                      className={`w-full py-4 border text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-100 text-stone-500 hover:bg-stone-50'}`}
                    >
                      Keep as Draft
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cancel Confirmation Modal */}
          {showCancelModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
              <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setShowCancelModal(false)} />
              <div style={cardStyle} className={`relative w-full max-w-sm p-10 shadow-2xl border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                <div className="text-center space-y-6">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isDarkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
                    <AlertCircle size={32} className="text-amber-500" />
                  </div>
                  <div className="space-y-2">
                    <h3 className={`text-2xl font-serif font-bold ${theme.text}`}>Unsaved Changes</h3>
                    <p className={`text-sm opacity-60 ${theme.textMuted}`}>Do you want to save your work as a draft before closing?</p>
                  </div>
                  <div className="flex flex-col gap-3 pt-4">
                    <button 
                      onClick={() => {
                        handleSavePost('draft');
                        closeEditor();
                      }}
                      style={pillStyle}
                      className="w-full py-4 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-[0.2em] hover:opacity-90 transition-all shadow-xl"
                    >
                      Save & Close
                    </button>
                    <button 
                      onClick={closeEditor}
                      style={pillStyle}
                      className={`w-full py-4 border text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${isDarkMode ? 'border-red-500/20 text-red-400 hover:bg-red-500/10' : 'border-red-100 text-red-500 hover:bg-red-50'}`}
                    >
                      Discard & Close
                    </button>
                    <button 
                      onClick={() => setShowCancelModal(false)}
                      className="w-full py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors mt-2"
                    >
                      Continue Editing
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Helper for dynamic greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Reflecting this morning, Hafiz?';
    if (hour < 17) return 'Good afternoon, Hafiz.';
    if (hour < 21) return 'Good evening, Hafiz.';
    return 'Deep in thought tonight, Hafiz?';
  };

  // Bento Card Component
  const BentoCard = ({ title, desc, subtext, icon: Icon, stat, onClick, onAdd, accentColor, className = '', children }) => (
    <div 
      onClick={onClick}
      className={`relative group overflow-hidden border border-stone-200/40 dark:border-stone-800/40 bg-stone-500/5 p-6 rounded-2xl flex flex-col justify-between active:scale-[0.98] transition-all duration-300 cursor-pointer hover:border-stone-400 dark:hover:border-stone-600 shadow-sm ${className}`}
    >
      <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl opacity-10 transition-opacity group-hover:opacity-20 ${accentColor}`} />
      
      <div className="flex justify-between items-start">
        <div className={`p-2.5 rounded-xl bg-stone-500/10 ${theme.text}`}>
          <Icon size={18} />
        </div>
        {onAdd && (
          <button 
            onClick={(e) => { e.stopPropagation(); onAdd(); }}
            style={pillStyle}
            className={`p-2 rounded-full bg-stone-500/10 hover:bg-stone-500/20 active:scale-95 transition-all text-xs ${theme.text}`}
            title={`Quick Add ${title}`}
          >
            <Plus size={14} />
          </button>
        )}
      </div>

      <div className="mt-auto pt-6 space-y-1.5">
        <h4 className={`text-base font-serif font-bold leading-tight tracking-tight ${theme.text}`}>{title}</h4>
        <p className={`text-[9px] opacity-60 font-bold uppercase tracking-wider ${theme.textMuted}`}>{stat}</p>
        <p className={`text-[10px] opacity-40 line-clamp-1 ${theme.textMuted}`}>{desc}</p>
        {children}
        {subtext && (
          <p className={`text-[9px] opacity-30 font-bold uppercase tracking-wider mt-1 ${theme.textMuted}`}>{subtext}</p>
        )}
      </div>
    </div>
  );

  return (
    <section className="w-full max-w-5xl mx-auto px-4 md:px-8 min-h-screen pt-12 md:pt-16 pb-24 animate-in fade-in slide-in-from-bottom-4 duration-1000 flex flex-col gap-8">
        
        {/* Workspace Hub Dashboard (Bento Grid for Desktop, Tablet, & Mobile) */}
        {(adminTab === 'hub' || mobileWorkspace === 'hub') && (
          <div className={`flex-1 py-8 md:py-12 space-y-8 md:space-y-12 animate-in fade-in duration-700 ${adminTab === 'hub' ? 'block' : 'hidden md:hidden'}`}>
            {/* Header Greeting & Database Status */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-6 border-stone-200/60 dark:border-stone-800/60">
              <div className="space-y-2">
                <h3 className={`text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight leading-tight ${theme.text}`}>
                  {getGreeting()}
                </h3>
                <p className={`text-xs md:text-sm opacity-50 ${theme.textMuted}`}>
                  Welcome to your central Idiawisdom writing and control hub.
                </p>
              </div>
              <div className="flex items-center gap-3 self-start md:self-center">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-widest opacity-60 ${theme.text}`}>
                  Database Connected & Live
                </span>
              </div>
            </div>

            {/* Responsive Symmetric Bento Grid (All Cards Equal Size) */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              
              {/* Card 1: Reflections */}
              <BentoCard 
                title="Reflections"
                stat={`${posts.length} entries archived`}
                desc="Essays, philosophical thoughts, and long-form journal reflections."
                subtext={`Last entry: ${getLastWrittenDate(posts)}`}
                icon={PenTool}
                onClick={() => { setMobileWorkspace('blog'); setAdminTab('blog'); }}
                onAdd={() => openNewPostEditor()}
                accentColor="bg-stone-400 dark:bg-stone-600"
                className="col-span-1 aspect-square md:h-[220px]"
              />

              {/* Card 2: Poetry Studio */}
              <BentoCard 
                title="Poetry Studio"
                stat={`${poems.length} verses written`}
                desc="Compose structural rhymes, stanzas, and micro-verse collections."
                subtext={`Last entry: ${getLastWrittenDate(poems)}`}
                icon={Feather}
                onClick={() => { setMobileWorkspace('poems'); setAdminTab('poems'); }}
                onAdd={() => setShowPoemForm(true)}
                accentColor="bg-stone-400 dark:bg-stone-600"
                className="col-span-1 aspect-square md:h-[220px]"
              />

              {/* Card 3: Sanctuary of Quotes */}
              <BentoCard 
                title="Sanctuary of Quotes"
                stat={`${quotes.length} insights saved`}
                desc="Record standalone snippets of wisdom and sudden sparks of inspiration."
                subtext={`Last entry: ${getLastWrittenDate(quotes)}`}
                icon={Quote}
                onClick={() => { setMobileWorkspace('quotes'); setAdminTab('quotes'); }}
                onAdd={() => setShowQuoteForm(true)}
                accentColor="bg-stone-400 dark:bg-stone-600"
                className="col-span-1 aspect-square md:h-[220px]"
              />

              {/* Card 4: Insights & Traffic */}
              <BentoCard 
                title="Insights & Traffic"
                stat="Analytics Monitor"
                desc="Real-time dashboard visualizing live visitor counts, entry clicks, and traffic logs."
                subtext="Real-time traffic feed"
                icon={Activity}
                onClick={() => { setMobileWorkspace('analytics'); setAdminTab('analytics'); }}
                accentColor="bg-stone-400 dark:bg-stone-600"
                className="col-span-1 aspect-square md:h-[220px]"
              >
                <div className="py-2 flex items-center justify-between">
                  <span className={`text-[10px] font-mono opacity-50 ${theme.text}`}>7D Trend</span>
                  {renderSparklineSVG()}
                </div>
              </BentoCard>

              {/* Card 5: Database Backup */}
              <BentoCard 
                title="Database Backup"
                stat="Google Drive Vault"
                desc="Securely pack Firestore collections and upload timestamped snapshots straight to your personal Drive."
                subtext={driveBackups && driveBackups.length > 0 ? `Last sync: ${formatDateToDDMMYYYY(driveBackups[0].createdTime)}` : 'No backups synced yet'}
                icon={Database}
                onClick={() => { setMobileWorkspace('backup'); setAdminTab('backup'); }}
                accentColor="bg-stone-400 dark:bg-stone-600"
                className="col-span-1 aspect-square md:h-[220px]"
              />

              {/* Card 6: Reading Diary */}
              <BentoCard 
                title="Reading Diary"
                stat={`${(diary || []).length} reflections`}
                desc="Write daily reflections from reading books & articles."
                subtext={`Last entry: ${getLastWrittenDate(diary)}`}
                icon={BookOpen}
                onClick={() => { setMobileWorkspace('diary'); setAdminTab('diary'); }}
                onAdd={() => { setEditingDiary(null); diaryEditor.openNewDiaryEditor(); setShowDiaryModal(true); }}
                accentColor="bg-stone-400 dark:bg-stone-600"
                className="col-span-1 aspect-square md:h-[220px]"
              />

              {/* Card 7: Bank Idea */}
              <BentoCard 
                title="Bank Idea"
                stat={`${(ideas || []).length} mind maps`}
                desc="Create and manage interactive mind maps of ideas and solutions."
                subtext={`Last updated: ${getLastWrittenDate(ideas)}`}
                icon={Lightbulb}
                onClick={() => { setMobileWorkspace('ideas'); setAdminTab('ideas'); }}
                onAdd={() => { setShowIdeaEditor(true); setEditingIdea(null); }}
                accentColor="bg-stone-400 dark:bg-stone-600"
                className="col-span-1 aspect-square md:h-[220px]"
              />
            </div>
          </div>
        )}

        {/* Main Workspace Content */}
        <main className={`flex-1 min-w-0 pb-16 space-y-12 ${adminTab === 'hub' ? 'hidden md:hidden' : 'block'}`}>
          
          {/* Unified Breadcrumb — visible on all screen sizes when inside a section */}
          {(adminTab !== 'hub' || mobileWorkspace !== 'hub') && (
            <div className={`flex items-center gap-2 mb-8 px-1 py-4 border-b sticky top-24 z-10 ${
              isDarkMode ? 'bg-stone-900/80 border-stone-800/80' : 'bg-white/80 border-stone-100/80'
            }`}>
              {/* Home / Hub link */}
              <button
                onClick={() => { setAdminTab('hub'); setMobileWorkspace('hub'); }}
                className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] transition-all opacity-50 hover:opacity-100 ${theme.text}`}
              >
                <ChevronLeft size={14} className="flex-shrink-0" />
                <span>Workspace Hub</span>
              </button>
              
              <span className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-20 ${theme.text}`}>/</span>
              
              {/* Current section name (non-clickable, just for display) */}
              <span className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-80 ${theme.text}`}>
                {(() => {
                  const labels = {
                    blog: 'Reflections',
                    quotes: 'Sanctuary of Quotes',
                    poems: 'Poetry Studio',
                    backup: 'Backup Vault',
                    analytics: 'Insights & Traffic',
                    diary: 'Reading Diary',
                    ideas: 'Bank Idea'
                  };
                  return labels[adminTab] || labels[mobileWorkspace] || '';
                })()}
              </span>
            </div>
          )}

        {adminTab === 'analytics' && (
          <div className="space-y-8 animate-in fade-in zoom-in-95 duration-500">
            <div className="flex items-center justify-between">
              <h3 className={`text-xl font-serif ${theme.text}`}>Traffic & Insights</h3>
              <div className="flex flex-wrap items-center justify-end gap-3 md:gap-6">
                <button onClick={() => setShowAdminVisits(!showAdminVisits)} className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest transition-all ${showAdminVisits ? 'opacity-100 text-amber-500' : `opacity-50 hover:opacity-100 ${theme.text}`}`}>
                  {showAdminVisits ? <ToggleRight size={18} /> : <ToggleLeft size={18} />} {showAdminVisits ? 'Include Admin' : 'Exclude Admin'}
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={downloadCSV} style={pillStyle} className={`px-4 py-2 border text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 opacity-50 hover:opacity-100 transition-opacity ${isDarkMode ? 'border-stone-800' : 'border-stone-200'}`}>
                    <Download size={12} /> <span className="hidden sm:inline">Export</span>
                  </button>
                  <button onClick={fetchAnalytics} style={pillStyle} className={`px-4 py-2 border text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 opacity-50 hover:opacity-100 transition-opacity ${isDarkMode ? 'border-stone-800' : 'border-stone-200'}`}>
                    <RefreshCw size={12} className={isAnalyticsLoading ? "animate-spin" : ""} /> <span className="hidden sm:inline">Refresh</span>
                  </button>
                </div>
              </div>
            </div>
            
            {isAnalyticsLoading && analyticsData.length === 0 ? (
              <div className="p-20 text-center opacity-50 flex flex-col items-center">
                <Loader2 size={32} className="animate-spin mb-4" />
                <p className="text-[10px] font-bold uppercase tracking-widest">Loading Insights...</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col xl:flex-row xl:items-center gap-4 mb-6">
                  <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar w-full xl:w-auto">
                    <button onClick={() => setAnalyticsTypeFilter('all')} style={pillStyle} className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${analyticsTypeFilter === 'all' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `border border-stone-200 dark:border-stone-800 opacity-50 hover:opacity-100 ${theme.text}`}`}>All Content</button>
                    <button onClick={() => setAnalyticsTypeFilter('blog')} style={pillStyle} className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${analyticsTypeFilter === 'blog' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `border border-stone-200 dark:border-stone-800 opacity-50 hover:opacity-100 ${theme.text}`}`}>Reflections</button>
                    <button onClick={() => setAnalyticsTypeFilter('poems')} style={pillStyle} className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${analyticsTypeFilter === 'poems' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `border border-stone-200 dark:border-stone-800 opacity-50 hover:opacity-100 ${theme.text}`}`}>Poems</button>
                    <button onClick={() => setAnalyticsTypeFilter('quotes')} style={pillStyle} className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${analyticsTypeFilter === 'quotes' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `border border-stone-200 dark:border-stone-800 opacity-50 hover:opacity-100 ${theme.text}`}`}>Quotes</button>
                  </div>
                  
                  <div className="hidden xl:block w-px h-6 bg-stone-200 dark:bg-stone-800" />
                  
                  <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar w-full xl:w-auto">
                    <button onClick={() => setAnalyticsTimeFilter('today')} style={pillStyle} className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${analyticsTimeFilter === 'today' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `border border-stone-200 dark:border-stone-800 opacity-50 hover:opacity-100 ${theme.text}`}`}>Today</button>
                    <button onClick={() => setAnalyticsTimeFilter('week')} style={pillStyle} className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${analyticsTimeFilter === 'week' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `border border-stone-200 dark:border-stone-800 opacity-50 hover:opacity-100 ${theme.text}`}`}>7 Days</button>
                    <button onClick={() => setAnalyticsTimeFilter('month')} style={pillStyle} className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${analyticsTimeFilter === 'month' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `border border-stone-200 dark:border-stone-800 opacity-50 hover:opacity-100 ${theme.text}`}`}>30 Days</button>
                    <button onClick={() => setAnalyticsTimeFilter('all')} style={pillStyle} className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${analyticsTimeFilter === 'all' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `border border-stone-200 dark:border-stone-800 opacity-50 hover:opacity-100 ${theme.text}`}`}>All Time</button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div style={cardStyle} className={`p-8 border ${isDarkMode ? 'bg-stone-900/40 border-stone-800' : 'bg-white border-stone-100'} shadow-xl relative overflow-hidden group`}>
                    <BarChart2 size={100} className={`absolute -right-4 -bottom-4 opacity-5 transition-transform group-hover:scale-110 ${theme.text}`} />
                    <p className={`text-[10px] font-bold uppercase tracking-widest opacity-50 mb-2 ${theme.text}`}>Total Views</p>
                    <p className={`text-5xl font-serif ${theme.text}`}>{filteredAnalytics.length}</p>
                    <p className={`text-[10px] font-bold uppercase tracking-widest opacity-40 mt-2 ${theme.text}`}>{uniqueVisitors} Unique Visitors</p>
                  </div>
                  
                  <div style={cardStyle} className={`p-8 border ${isDarkMode ? 'bg-stone-900/40 border-stone-800' : 'bg-white border-stone-100'} shadow-xl relative overflow-hidden group`}>
                    <Activity size={100} className={`absolute -right-4 -bottom-4 opacity-5 transition-transform group-hover:scale-110 text-emerald-500`} />
                    <p className={`text-[10px] font-bold uppercase tracking-widest opacity-50 mb-2 ${theme.text}`}>WhatsApp Shares</p>
                    <p className={`text-5xl font-serif ${theme.text}`}>
                      {filteredAnalytics.filter(d => d.source === 'whatsapp').length}
                    </p>
                  </div>

                  <div style={cardStyle} className={`p-8 border ${isDarkMode ? 'bg-stone-900/40 border-stone-800' : 'bg-white border-stone-100'} shadow-xl relative overflow-hidden group`}>
                    <p className={`text-[10px] font-bold uppercase tracking-widest opacity-50 mb-2 ${theme.text}`}>Top Location</p>
                    <p className={`text-2xl font-serif mt-2 line-clamp-2 ${theme.text}`}>
                      {topLocation}
                    </p>
                  </div>
                </div>

                {/* Global Heatmap Visualization */}
                <div style={cardStyle} className={`mt-8 p-4 sm:p-8 border overflow-hidden ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
                    <p className={`text-[10px] font-bold uppercase tracking-widest opacity-50 ${theme.text}`}>Global Reach</p>
                    <div className="flex flex-wrap items-center gap-3">
                      {/* Region Filter Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setIsRegionFilterOpen(prev => !prev)}
                          style={pillStyle}
                          className={`flex items-center gap-2 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-300 border whitespace-nowrap ${
                            mapRegionFilter !== 'all'
                              ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                              : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                          }`}
                        >
                          <span>{mapRegionFilter === 'all' ? 'All Regions' : mapRegionFilter === 'my' ? 'Malaysia' : 'International'}</span>
                          <ChevronDown size={14} className={`${isRegionFilterOpen ? 'rotate-180' : ''} transition-transform`} />
                        </button>

                        {isRegionFilterOpen && (
                          <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300" onClick={() => setIsRegionFilterOpen(false)}>
                            <div className="absolute inset-0 bg-black/20 backdrop-blur-md" />
                            <div style={cardStyle} className={`relative w-full max-w-xs mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`} onClick={(e) => e.stopPropagation()}>
                              <div className="flex flex-col gap-2">
                                <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Region</p>
                                {[
                                  { value: 'all', label: 'All Regions' },
                                  { value: 'my', label: 'Malaysia Only' },
                                  { value: 'intl', label: 'International' },
                                ].map(option => (
                                  <button key={option.value} onClick={() => { setMapRegionFilter(option.value); setIsRegionFilterOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all ${mapRegionFilter === option.value ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>{option.label}</button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Time Filter Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setIsMapFilterOpen(prev => !prev)}
                          style={pillStyle}
                          className={`flex items-center gap-2 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-300 border whitespace-nowrap ${
                            mapTimeFilter !== 'all'
                              ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                              : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                          }`}
                        >
                          <span>
                            {mapTimeFilter === 'all' ? 'All Time' : mapTimeFilter === 'month' ? 'This Month' : 'This Week'}
                          </span>
                          <ChevronDown size={14} className={`${isMapFilterOpen ? 'rotate-180' : ''} transition-transform`} />
                        </button>

                        {isMapFilterOpen && (
                          <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300" onClick={() => setIsMapFilterOpen(false)}>
                            <div className="absolute inset-0 bg-black/20 backdrop-blur-md" />
                            <div style={cardStyle} className={`relative w-full max-w-xs mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`} onClick={(e) => e.stopPropagation()}>
                              <div className="flex flex-col gap-2">
                                <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Time</p>
                                {[
                                  { value: 'all', label: 'All Time' },
                                  { value: 'month', label: 'This Month' },
                                  { value: 'week', label: 'This Week' },
                                ].map(option => (
                                  <button key={option.value} onClick={() => { setMapTimeFilter(option.value); setIsMapFilterOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all ${mapTimeFilter === option.value ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>{option.label}</button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <LocationMap data={mapFilteredAnalytics} isDarkMode={isDarkMode} theme={theme} regionFilter={mapRegionFilter} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div style={cardStyle} className={`p-8 border ${isDarkMode ? 'bg-stone-900/40 border-stone-800' : 'bg-white border-stone-100'} shadow-xl relative overflow-hidden group`}>
                    <p className={`text-[10px] font-bold uppercase tracking-widest opacity-50 mb-6 ${theme.text}`}>Device Breakdown</p>
                    <div className="flex items-end justify-between mb-3">
                      <div>
                        <span className={`text-3xl font-serif ${theme.text}`}>{deviceStats.mobile}%</span>
                        <span className={`text-[10px] font-bold uppercase tracking-widest opacity-50 ml-2 ${theme.text}`}>Mobile</span>
                      </div>
                      <div className="text-right">
                        <span className={`text-[10px] font-bold uppercase tracking-widest opacity-50 mr-2 ${theme.text}`}>Desktop</span>
                        <span className={`text-3xl font-serif ${theme.text}`}>{deviceStats.desktop}%</span>
                      </div>
                    </div>
                    <div className={`w-full h-2 rounded-full overflow-hidden flex ${isDarkMode ? 'bg-stone-800' : 'bg-stone-100'}`}>
                      <div className="bg-amber-500 h-full transition-all duration-1000" style={{ width: `${deviceStats.mobile}%` }} title="Mobile" />
                      <div className="bg-stone-400 h-full transition-all duration-1000" style={{ width: `${deviceStats.desktop}%` }} title="Desktop" />
                    </div>
                  </div>

                  <div style={cardStyle} className={`p-8 border ${isDarkMode ? 'bg-stone-900/40 border-stone-800' : 'bg-white border-stone-100'} shadow-xl relative overflow-hidden group`}>
                    <p className={`text-[10px] font-bold uppercase tracking-widest opacity-50 mb-6 ${theme.text}`}>Traffic Sources</p>
                    <div className="flex items-end justify-between mb-3">
                      <div>
                        <span className={`text-3xl font-serif text-emerald-500`}>{sourceStats.whatsapp}%</span>
                        <span className={`text-[10px] font-bold uppercase tracking-widest opacity-50 ml-2 ${theme.text}`}>WhatsApp</span>
                      </div>
                      <div className="text-right">
                        <span className={`text-[10px] font-bold uppercase tracking-widest opacity-50 mr-2 ${theme.text}`}>Direct</span>
                        <span className={`text-3xl font-serif ${theme.text}`}>{sourceStats.direct}%</span>
                      </div>
                    </div>
                    <div className={`w-full h-2 rounded-full overflow-hidden flex ${isDarkMode ? 'bg-stone-800' : 'bg-stone-100'}`}>
                      <div className="bg-emerald-500 h-full transition-all duration-1000" style={{ width: `${sourceStats.whatsapp}%` }} title="WhatsApp" />
                      <div className="bg-stone-400 h-full transition-all duration-1000" style={{ width: `${sourceStats.direct}%` }} title="Direct Link" />
                      <div className="bg-stone-600 h-full transition-all duration-1000" style={{ width: `${sourceStats.other}%` }} title="Other (Socials, etc)" />
                    </div>
                  </div>
                </div>

                {chartData.length > 0 && (
                  <div style={cardStyle} className={`mt-8 p-8 border ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                    <div className="flex items-center justify-between mb-12">
                      <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>Traffic Spikes</span>
                    </div>
                    <div className="h-56 flex items-end gap-1 md:gap-2 pb-6">
                      {chartData.map((d, i) => {
                        // Intelligently hide some labels if there are too many columns (like 30 Days)
                        const showLabel = chartData.length > 14 ? i % Math.ceil(chartData.length / 7) === 0 : true;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center justify-end group relative h-full w-full">
                            <div className={`absolute -top-12 opacity-0 group-hover:opacity-100 transition-opacity text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border ${isDarkMode ? 'bg-stone-800 border-stone-700 text-white' : 'bg-stone-900 border-stone-800 text-white'} pointer-events-none whitespace-nowrap z-10 shadow-xl`}>
                              {d.count} views
                            </div>
                            <div 
                              className={`w-full rounded-t-sm transition-all duration-1000 ${d.count > 0 ? (isDarkMode ? 'bg-stone-600 group-hover:bg-stone-400' : 'bg-stone-300 group-hover:bg-stone-500') : 'bg-transparent'}`}
                              style={{ height: `${(d.count / maxChartValue) * 100}%`, minHeight: d.count > 0 ? '4px' : '0' }}
                            />
                            {showLabel && (
                              <span className={`absolute -bottom-8 text-[8px] font-bold uppercase tracking-widest opacity-40 ${theme.text} truncate text-center w-20`}>
                                {d.label}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {topContent.length > 0 && (
                  <div style={cardStyle} className={`mt-8 p-8 border ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                    <div className="flex items-center justify-between mb-8">
                      <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>Top Viewed Content</span>
                    </div>
                    <div className="space-y-3">
                      {topContent.map((item, idx) => (
                        <div key={item.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-5 rounded-2xl border transition-colors gap-4 sm:gap-0 ${isDarkMode ? 'border-stone-800/50 bg-stone-800/20 hover:bg-stone-800/40' : 'border-stone-100 bg-stone-50/50 hover:bg-stone-100/50'}`}>
                          <div className="flex items-start sm:items-center gap-4 sm:gap-5 pr-0 sm:pr-4">
                            <span className={`text-xl font-serif opacity-30 w-4 mt-0.5 sm:mt-0 flex-shrink-0 ${theme.text}`}>#{idx + 1}</span>
                            <div className="space-y-1">
                              <h4 className={`font-serif text-lg break-words line-clamp-1 ${item.type === 'quote' ? 'italic' : ''} ${theme.text}`}>{item.title}</h4>
                              <p className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>
                                {item.type === 'post' ? 'Reflection' : `Quote — ${item.author || 'Ben Hasan'}`}
                              </p>
                            </div>
                          </div>
                          <div className={`self-start sm:self-auto ml-8 sm:ml-0 text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full border ${isDarkMode ? 'border-stone-700 bg-stone-800 text-stone-300' : 'border-stone-200 bg-white text-stone-600'} flex-shrink-0`}>
                            {item.count} views
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Most Loved Sections */}
                {(topLovedReflections.length > 0 || topLovedQuotes.length > 0) && (
                  <div className={`grid grid-cols-1 ${analyticsTypeFilter === 'all' ? 'xl:grid-cols-2' : ''} gap-6 mt-8`}>
                    {analyticsTypeFilter !== 'quotes' && topLovedReflections.length > 0 && (
                      <div style={cardStyle} className={`p-8 border ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                        <div className="flex items-center justify-between mb-8">
                          <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>Most Loved Reflections</span>
                          <Heart size={14} className="text-red-500 fill-red-500" />
                        </div>
                        <div className="space-y-3">
                          {topLovedReflections.map((post, idx) => (
                            <div key={post.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-5 rounded-2xl border transition-colors gap-4 sm:gap-0 ${isDarkMode ? 'border-stone-800/50 bg-stone-800/20 hover:bg-stone-800/40' : 'border-stone-100 bg-stone-50/50 hover:bg-stone-100/50'}`}>
                              <div className="flex items-start sm:items-center gap-4 sm:gap-5 pr-0 sm:pr-4">
                                <span className={`text-xl font-serif opacity-30 w-4 mt-0.5 sm:mt-0 flex-shrink-0 ${theme.text}`}>#{idx + 1}</span>
                                <h4 className={`font-serif text-base break-words line-clamp-2 ${theme.text}`}>{post.title}</h4>
                              </div>
                              <div className={`self-start sm:self-auto ml-8 sm:ml-0 text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full border ${isDarkMode ? 'border-stone-700 bg-stone-800 text-stone-300' : 'border-stone-200 bg-white text-stone-600'} flex-shrink-0 flex items-center gap-2`}>
                                <Heart size={10} className="fill-red-500 text-red-500" /> {post.likes}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {analyticsTypeFilter !== 'blog' && topLovedQuotes.length > 0 && (
                      <div style={cardStyle} className={`p-8 border ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                        <div className="flex items-center justify-between mb-8">
                          <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>Most Loved Quotes</span>
                          <Heart size={14} className="text-red-500 fill-red-500" />
                        </div>
                        <div className="space-y-3">
                          {topLovedQuotes.map((quote, idx) => (
                            <div key={quote.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-5 rounded-2xl border transition-colors gap-4 sm:gap-0 ${isDarkMode ? 'border-stone-800/50 bg-stone-800/20 hover:bg-stone-800/40' : 'border-stone-100 bg-stone-50/50 hover:bg-stone-100/50'}`}>
                              <div className="flex items-start sm:items-center gap-4 sm:gap-5 pr-0 sm:pr-4">
                                <span className={`text-xl font-serif opacity-30 w-4 mt-0.5 sm:mt-0 flex-shrink-0 ${theme.text}`}>#{idx + 1}</span>
                                <div className="space-y-1">
                                  <h4 className={`font-serif text-base break-words line-clamp-1 italic ${theme.text}`}>"{quote.text}"</h4>
                                  <p className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>— {quote.author}</p>
                                </div>
                              </div>
                              <div className={`self-start sm:self-auto ml-8 sm:ml-0 text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full border ${isDarkMode ? 'border-stone-700 bg-stone-800 text-stone-300' : 'border-stone-200 bg-white text-stone-600'} flex-shrink-0 flex items-center gap-2`}>
                                <Heart size={10} className="fill-red-500 text-red-500" /> {quote.likes}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div style={cardStyle} className={`mt-8 border overflow-hidden divide-y ${isDarkMode ? 'bg-stone-900/20 border-stone-800 divide-stone-800' : 'bg-white border-stone-100 divide-stone-100 shadow-xl'}`}>
                  <div className={`px-6 py-4 flex items-center justify-between border-b ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
                    <button 
                      onClick={handleSelectAll}
                      className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity ${theme.text}`}
                    >
                      {paginatedAnalytics.length > 0 && paginatedAnalytics.every(i => selectedItems.has(i.id)) ? <CheckSquare size={14} /> : <Square size={14} />} Select All
                    </button>
                    <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>Recent Traffic Feed</span>
                  </div>
                  {paginatedAnalytics.map((view, idx) => (
                    <div key={view.id || idx} className="p-6 flex items-start lg:items-center hover:bg-stone-500/5 transition-colors">
                      <button onClick={(e) => { e.stopPropagation(); toggleSelection(view.id); }} className={`mr-4 mt-1 lg:mt-0 flex-shrink-0 opacity-40 hover:opacity-100 transition-opacity ${selectedItems.has(view.id) ? 'text-amber-500 opacity-100' : theme.text}`}>
                        {selectedItems.has(view.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                      </button>
                      <div className="flex-1 flex flex-col lg:flex-row lg:justify-between lg:items-center overflow-hidden gap-4 lg:gap-0">
                        <div className="space-y-2 lg:space-y-1">
                          <h4 className={`font-serif text-lg break-all md:break-normal ${theme.text}`}>
                            {(!view.path || view.path === '/') ? 'Home Page' : 
                             (view.path?.includes('?quote=') || view.path?.includes('/quote/')) ? view.path.replace(/\/?\?quote=|\/quote\//, 'Quote ID: ') : 
                             view.path?.replace('/?p=', 'Post ID: ')}
                          </h4>
                          <p className={`text-[10px] font-bold uppercase tracking-widest opacity-40 leading-relaxed ${theme.text}`}>
                            {view.timestamp?.toDate ? view.timestamp.toDate().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'Just now'} • {view.city !== 'Unknown' && view.city ? `${view.city}, ` : ''}{view.country}
                          </p>
                        </div>
                        <div className="flex flex-row flex-wrap lg:flex-col items-center lg:items-end gap-3 lg:gap-2 flex-shrink-0 lg:ml-4">
                          <div className="flex items-center gap-2">
                            <span className={`text-[9px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border ${
                              view.source === 'whatsapp' ? 'border-emerald-500/30 text-emerald-500 bg-emerald-500/10' : 
                              view.source === 'direct' ? 'border-blue-500/30 text-blue-500 bg-blue-500/10' : 
                              'border-violet-500/30 text-violet-500 bg-violet-500/10'
                            }`}>
                              {view.source === 'direct' ? 'Direct Link' : view.source}
                            </span>
                            {view.role === 'admin' && (
                              <span className={`text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20`}>Admin</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {view.device && <span className={`text-[8px] font-bold uppercase tracking-widest opacity-30 ${theme.text}`}>{view.device}</span>}
                            <button onClick={() => { setItemToDelete({ id: view.id, name: (!view.path || view.path === '/') ? 'Home Page View' : view.path, type: 'analytics', collection: 'page_views' }); setShowDeleteModal(true); }} className="text-stone-500 opacity-30 hover:opacity-100 hover:text-red-500 transition-all" title="Delete record">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Analytics Pagination Controls */}
                {totalAnalyticsPages > 1 && (
                  <div className="flex items-center justify-center gap-6 mt-8 animate-in fade-in slide-in-from-bottom-2">
                    <button 
                      onClick={() => setAnalyticsCurrentPage(p => Math.max(1, p - 1))}
                      disabled={analyticsCurrentPage === 1}
                      className={`p-2 rounded-full transition-all ${analyticsCurrentPage === 1 ? 'opacity-20 cursor-not-allowed' : `opacity-50 hover:opacity-100 ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}`}
                    >
                      <ChevronLeft size={20} className={theme.text} />
                    </button>
                    
                    <span className={`text-xs font-bold uppercase tracking-widest ${theme.text} opacity-60`}>
                      Page {analyticsCurrentPage} of {totalAnalyticsPages}
                    </span>

                    <button 
                      onClick={() => setAnalyticsCurrentPage(p => Math.min(totalAnalyticsPages, p + 1))}
                      disabled={analyticsCurrentPage === totalAnalyticsPages}
                      className={`p-2 rounded-full transition-all ${analyticsCurrentPage === totalAnalyticsPages ? 'opacity-20 cursor-not-allowed' : `opacity-50 hover:opacity-100 ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}`}
                    >
                      <ChevronRight size={20} className={theme.text} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {adminTab === 'backup' && (
          <div className="space-y-8 animate-in fade-in zoom-in-95 duration-500">
            <div className="flex items-center justify-between border-b pb-6 border-stone-200 dark:border-stone-800">
              <div className="space-y-1">
                <h3 className={`text-xl font-serif ${theme.text}`}>Backup Vault</h3>
                <p className={`text-xs opacity-50 ${theme.textMuted}`}>Sync database archives directly to your personal Google Drive</p>
              </div>
              <div className="flex items-center gap-2">
                {googleAccessToken && (
                  <button 
                    onClick={async () => {
                      const res = await performBackup(googleAccessToken, { posts, quotes, poems, categories });
                      if (res.success) {
                        showToast("Database backup successfully archived!");
                      } else {
                        showToast(`Backup failed: ${res.error}`, "error");
                      }
                    }} 
                    disabled={isBackupLoading}
                    style={pillStyle} 
                    className={`px-5 py-2.5 bg-stone-900 dark:bg-stone-800 text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 hover:opacity-90 transition-opacity ${isBackupLoading ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    {isBackupLoading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} 
                    <span>{isBackupLoading ? 'Archiving...' : 'Run Backup'}</span>
                  </button>
                )}
              </div>
            </div>

            {!googleAccessToken ? (
              <div style={cardStyle} className={`p-12 text-center border flex flex-col items-center justify-center space-y-6 ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                <div className="p-4 rounded-full bg-stone-500/5 text-stone-400">
                  <Database size={40} className="stroke-[1.5]" />
                </div>
                <div className="space-y-2 max-w-md">
                  <h4 className={`font-serif text-lg ${theme.text}`}>Google Drive Connection Required</h4>
                  <p className={`text-xs opacity-50 leading-relaxed ${theme.textMuted}`}>
                    To back up your entries, quotes, and poetry studio verses directly into your Google Drive, we need a secure, temporary authorization.
                  </p>
                </div>
                <button
                  onClick={handleConnectDrive}
                  style={pillStyle}
                  className={`px-6 py-3 bg-stone-900 dark:bg-stone-800 text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 hover:opacity-90 transition-opacity`}
                >
                  <Fingerprint size={14} /> Connect Google Drive
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Backup Console Logs */}
                <div style={cardStyle} className={`p-8 border flex flex-col h-[400px] ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                  <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 mb-4 ${theme.text}`}>Backup Console</span>
                  
                  <div className="flex-1 overflow-y-auto no-scrollbar font-mono text-[10px] p-4 rounded-xl border border-stone-200/50 dark:border-stone-800/50 bg-stone-500/5 space-y-2">
                    {backupLogs.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center opacity-40 py-12">
                        <FileJson size={24} className="mb-2" />
                        <p className="tracking-wide">SYSTEM READY. CLICK 'RUN BACKUP' TO INITIATE PROTOCOL.</p>
                      </div>
                    ) : (
                      backupLogs.map((log, idx) => (
                        <div key={idx} className={`${log.includes('❌') ? 'text-red-400' : log.includes('successfully') ? 'text-emerald-400' : 'opacity-85'}`}>
                          {log}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Stored Archives in Google Drive */}
                <div style={cardStyle} className={`p-8 border flex flex-col h-[400px] ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                  <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 mb-4 ${theme.text}`}>Google Drive Archives</span>

                  <div className="flex-1 overflow-y-auto no-scrollbar space-y-3">
                    {driveBackups.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center opacity-40 py-12">
                        <History size={24} className="mb-2" />
                        <p className="text-[10px] tracking-widest uppercase">No backup files detected.</p>
                        <p className="text-[9px] lowercase opacity-60">archives will list here once generated.</p>
                      </div>
                    ) : (
                      driveBackups.map((backup) => (
                        <div key={backup.id} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isDarkMode ? 'border-stone-800 bg-stone-800/20 hover:bg-stone-800/40' : 'border-stone-100 bg-stone-50 hover:bg-stone-100/50'}`}>
                          <div className="flex items-center gap-3 overflow-hidden">
                            <div className="p-2 rounded-lg bg-stone-500/5 text-stone-400 flex-shrink-0">
                              <FileJson size={16} />
                            </div>
                            <div className="overflow-hidden space-y-0.5">
                              <h4 className={`text-xs font-mono truncate ${theme.text}`} title={backup.name}>{backup.name}</h4>
                              <p className={`text-[8px] font-bold opacity-40 ${theme.text}`}>
                                {new Date(backup.createdTime).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                {backup.size && ` • ${(backup.size / 1024).toFixed(1)} KB`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => setRestoreConfirmBackup(backup)}
                              style={pillStyle}
                              className={`p-2 border rounded-full hover:bg-amber-500/10 hover:text-amber-500 hover:border-amber-500/20 transition-all ${isDarkMode ? 'border-stone-800 text-stone-400' : 'border-stone-200 text-stone-500'}`}
                              title="Restore database to this snapshot"
                            >
                              <RotateCcw size={14} />
                            </button>
                            <a 
                              href={backup.webViewLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={pillStyle}
                              className={`p-2 border rounded-full hover:bg-stone-500/10 transition-colors ${isDarkMode ? 'border-stone-800 text-stone-400' : 'border-stone-200 text-stone-500'}`}
                              title="View file in Google Drive"
                            >
                              <ExternalLink size={14} />
                            </a>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Database Health & Maintenance Console */}
              <div style={cardStyle} className={`p-8 border mt-8 ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="space-y-4 flex-1">
                    <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>Database Health & Status</span>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className={`p-4 rounded-xl border ${isDarkMode ? 'border-stone-800 bg-stone-800/10' : 'border-stone-100 bg-stone-50'}`}>
                        <p className={`text-[9px] font-bold uppercase tracking-widest opacity-40 ${theme.textMuted}`}>Reflections</p>
                        <p className={`text-xl font-serif font-bold ${theme.text}`}>{posts.length}</p>
                      </div>
                      <div className={`p-4 rounded-xl border ${isDarkMode ? 'border-stone-800 bg-stone-800/10' : 'border-stone-100 bg-stone-50'}`}>
                        <p className={`text-[9px] font-bold uppercase tracking-widest opacity-40 ${theme.textMuted}`}>Poems</p>
                        <p className={`text-xl font-serif font-bold ${theme.text}`}>{poems.length}</p>
                      </div>
                      <div className={`p-4 rounded-xl border ${isDarkMode ? 'border-stone-800 bg-stone-800/10' : 'border-stone-100 bg-stone-50'}`}>
                        <p className={`text-[9px] font-bold uppercase tracking-widest opacity-40 ${theme.textMuted}`}>Quotes</p>
                        <p className={`text-xl font-serif font-bold ${theme.text}`}>{quotes.length}</p>
                      </div>
                      <div className={`p-4 rounded-xl border ${isDarkMode ? 'border-stone-800 bg-stone-800/10' : 'border-stone-100 bg-stone-50'}`}>
                        <p className={`text-[9px] font-bold uppercase tracking-widest opacity-40 ${theme.textMuted}`}>Traffic Logs</p>
                        <p className={`text-xl font-serif font-bold ${theme.text}`}>{analyticsData.length}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4 md:w-80 flex-shrink-0">
                    <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>Maintenance Tools</span>
                    <div className={`p-5 rounded-2xl border text-left space-y-3 ${isDarkMode ? 'border-stone-800 bg-stone-800/10' : 'border-stone-100 bg-stone-50'}`}>
                      <div className="space-y-1">
                        <p className={`text-[10px] font-bold uppercase tracking-wider ${theme.text}`}>Prune Historical Logs</p>
                        <p className={`text-[9px] opacity-50 leading-relaxed ${theme.textMuted}`}>Purge visitor logs older than 180 days to keep Firestore compact.</p>
                      </div>
                      <button
                        onClick={pruneOldLogs}
                        disabled={isSaving}
                        style={pillStyle}
                        className={`w-full py-2.5 border text-[9px] font-bold uppercase tracking-widest transition-all ${isSaving ? 'opacity-50 cursor-wait' : ''} ${oldLogsCount > 0 ? (isDarkMode ? 'bg-amber-500/10 border-amber-500/20 text-amber-400 hover:bg-amber-500/20' : 'bg-amber-50 border-amber-100 text-amber-700 hover:bg-amber-100') : (isDarkMode ? 'border-stone-700 text-stone-500' : 'border-stone-200 text-stone-400')}`}
                      >
                        🧹 Prune {oldLogsCount} Old Logs
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          </div>
        )}

        {(adminTab === 'blog' || adminTab === 'quotes' || adminTab === 'poems' || adminTab === 'diary') && (
          <div className="animate-in fade-in duration-700 space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h3 className={`text-xl font-serif ${theme.text}`}>
                {adminTab === 'blog' ? 'Recent Writings' : adminTab === 'quotes' ? 'Quotes' : adminTab === 'poems' ? 'Poems' : 'Reading Diary'}
              </h3>
              <div className="flex items-center gap-2 md:gap-3">
                {adminTab === 'quotes' && (
                  <>
                    <button onClick={handleFindDuplicates} style={pillStyle} className={`p-2.5 md:px-5 md:py-2.5 border ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'} text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors`} title="Find Duplicates">
                      <Search size={14} /> <span className="hidden md:inline">Find Duplicates</span>
                    </button>
                    <button onClick={downloadQuotesCSV} style={pillStyle} className={`p-2.5 md:px-5 md:py-2.5 border ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'} text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors`} title="Export CSV">
                      <Download size={14} /> <span className="hidden md:inline">Export CSV</span>
                    </button>
                    <input type="file" accept=".csv" id="csv-upload" className="hidden" onChange={handleCSVUpload} disabled={isSaving} />
                    <label htmlFor="csv-upload" style={pillStyle} className={`p-2.5 md:px-5 md:py-2.5 border ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'} text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 cursor-pointer transition-colors ${isSaving ? 'opacity-50 cursor-wait' : ''}`}>
                      {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} <span className="hidden md:inline">{isSaving ? 'Importing...' : 'Import CSV'}</span>
                    </label>
                  </>
                )}
                {adminTab === 'poems' && (
                  <>
                    <button onClick={handleFindPoemDuplicates} style={pillStyle} className={`p-2.5 md:px-5 md:py-2.5 border ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'} text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors`} title="Find Duplicates">
                      <Search size={14} /> <span className="hidden md:inline">Find Duplicates</span>
                    </button>
                    <button onClick={downloadPoemsCSV} style={pillStyle} className={`p-2.5 md:px-5 md:py-2.5 border ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'} text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors`} title="Export CSV">
                      <Download size={14} /> <span className="hidden md:inline">Export CSV</span>
                    </button>
                    <input type="file" accept=".csv" id="poem-csv-upload" className="hidden" onChange={handlePoemCSVUpload} disabled={isSaving} />
                    <label htmlFor="poem-csv-upload" style={pillStyle} className={`p-2.5 md:px-5 md:py-2.5 border ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'} text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 cursor-pointer transition-colors ${isSaving ? 'opacity-50 cursor-wait' : ''}`}>
                      {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} <span className="hidden md:inline">{isSaving ? 'Importing...' : 'Import CSV'}</span>
                    </label>
                  </>
                )}
                {adminTab === 'diary' && (
                  <>
                    <button onClick={downloadDiaryCSV} style={pillStyle} className={`p-2.5 md:px-5 md:py-2.5 border ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'} text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-colors`} title="Export CSV">
                      <Download size={14} /> <span className="hidden md:inline">Export CSV</span>
                    </button>
                    <input type="file" accept=".csv" id="diary-csv-upload" className="hidden" onChange={handleDiaryCSVUpload} disabled={isSaving} />
                    <label htmlFor="diary-csv-upload" style={pillStyle} className={`p-2.5 md:px-5 md:py-2.5 border ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-200 text-stone-500 hover:bg-stone-50'} text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 cursor-pointer transition-colors ${isSaving ? 'opacity-50 cursor-wait' : ''}`}>
                      {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} <span className="hidden md:inline">{isSaving ? 'Importing...' : 'Import CSV'}</span>
                    </label>
                  </>
                )}
                <button 
                  onClick={
                    adminTab === 'blog' 
                      ? openNewPostEditor 
                      : adminTab === 'quotes' 
                        ? () => setShowQuoteForm(true) 
                        : adminTab === 'poems'
                          ? () => setShowPoemForm(true)
                          : () => { setEditingDiary(null); diaryEditor.openNewDiaryEditor(); setShowDiaryModal(true); }
                  } 
                  style={pillStyle} 
                  className="px-5 py-2.5 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 hover:opacity-80"
                >
                  <Plus size={14} /><span>Create New</span>
                </button>
              </div>
            </div>
            
            {adminTab === 'blog' && (
              <div className="space-y-8">
                {/* 1. Stats Chips (Draft/Published) */}
                <div className="flex flex-wrap items-center gap-4">
                  {/* Published Chip */}
                  <button
                    onClick={() => setStatusFilter('published')}
                    style={pillStyle}
                    className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                      statusFilter === 'published'
                        ? (isDarkMode ? 'bg-stone-800 border-stone-800 text-white' : 'bg-stone-900 border-stone-900 text-white')
                        : `border-stone-200 dark:border-stone-800 opacity-60 hover:opacity-100 ${theme.text}`
                    }`}
                  >
                    Published <span className="ml-2 opacity-60">{adminStats.published}</span>
                  </button>

                  {/* Drafts Chip */}
                  <button
                    onClick={() => setStatusFilter('draft')}
                    style={pillStyle}
                    className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                      statusFilter === 'draft'
                        ? (isDarkMode ? 'bg-stone-800 border-stone-800 text-white' : 'bg-stone-900 border-stone-900 text-white')
                        : `border-stone-200 dark:border-stone-800 opacity-60 hover:opacity-100 ${theme.text}`
                    }`}
                  >
                    Drafts <span className="ml-2 opacity-60">{adminStats.drafts}</span>
                  </button>
                </div>

                {/* 2. Search & Sort */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className={`flex-1 relative flex items-center px-5 py-3 rounded-full border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-200'}`}>
                    <Search size={16} className={`opacity-40 ${theme.text}`} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search reflections..."
                      className={`w-full bg-transparent border-none outline-none text-xs font-bold uppercase tracking-widest ml-3 ${theme.text} placeholder:opacity-30`}
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="ml-2 opacity-40 hover:opacity-100"><X size={14} /></button>
                    )}
                  </div>

                  <div className={`relative flex items-center px-5 py-3 rounded-full border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-200'}`}>
                    <span className={`text-[10px] font-bold uppercase tracking-widest opacity-40 mr-3 whitespace-nowrap ${theme.text}`}>Sort By</span>
                    <select 
                      value={sortBy} 
                      onChange={(e) => setSortBy(e.target.value)}
                      className={`bg-transparent border-none outline-none text-xs font-bold uppercase tracking-widest ${theme.text} cursor-pointer appearance-none pr-6`}
                    >
                      <option value="date-desc" className={isDarkMode ? 'bg-stone-900' : 'bg-white'}>Newest</option>
                      <option value="date-asc" className={isDarkMode ? 'bg-stone-900' : 'bg-white'}>Oldest</option>
                      <option value="title-asc" className={isDarkMode ? 'bg-stone-900' : 'bg-white'}>Title (A-Z)</option>
                      <option value="title-desc" className={isDarkMode ? 'bg-stone-900' : 'bg-white'}>Title (Z-A)</option>
                    </select>
                    <ChevronDown size={14} className={`absolute right-4 pointer-events-none opacity-40 ${theme.text}`} />
                  </div>
                </div>

                {/* 3. Categories (Horizontal scroll ribbon on mobile, wrapped grid on desktop) */}
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar w-full py-2 -mx-4 px-4 md:flex-wrap md:overflow-visible md:mx-0 md:px-0">
                  <button
                    onClick={() => setCategoryFilter('All')}
                    style={pillStyle}
                    className={`px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all border flex-shrink-0 ${categoryFilter === 'All' ? (isDarkMode ? 'bg-stone-800 border-stone-800 text-white' : 'bg-stone-900 border-stone-900 text-white') : `border-transparent opacity-40 hover:opacity-100 ${theme.text}`}`}
                  >
                    All <span className="ml-1.5 opacity-50">{categoryCounts.All}</span>
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setCategoryFilter(cat)}
                      style={pillStyle}
                      className={`px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all border flex-shrink-0 ${categoryFilter === cat ? (isDarkMode ? 'bg-stone-800 border-stone-800 text-white' : 'bg-stone-900 border-stone-900 text-white') : `border-transparent opacity-40 hover:opacity-100 ${theme.text}`}`}
                    >
                      {cat} <span className="ml-1.5 opacity-50">{categoryCounts[cat] || 0}</span>
                    </button>
                  ))}
                </div>

                {/* 4. List */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between px-2">
                    <h4 className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>
                      {statusFilter === 'all' ? 'All Posts' : statusFilter === 'draft' ? 'Drafts' : 'Published'}
                      <span className="ml-2 opacity-50">({filteredPosts.length})</span>
                    </h4>
                  </div>
                  
                  <div style={cardStyle} className={`border overflow-hidden divide-y ${isDarkMode ? 'bg-stone-900/20 border-stone-800 divide-stone-800' : 'bg-white border-stone-100 divide-stone-100 shadow-xl'}`}>
                    <div className={`px-6 py-4 flex items-center border-b ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
                      <button 
                        onClick={handleSelectAll}
                        className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity ${theme.text}`}
                      >
                        {adminTab === 'blog' && filteredPosts.length > 0 && filteredPosts.every(i => selectedItems.has(i.id)) ? <CheckSquare size={14} /> : <Square size={14} />} Select All
                      </button>
                    </div>
                    {paginatedPosts.length > 0 ? paginatedPosts.map((item) => (
                      <div key={item.id} className="p-4 md:p-6 flex justify-between items-center hover:bg-stone-500/5 transition-colors gap-3">
                        <button onClick={(e) => { e.stopPropagation(); toggleSelection(item.id); }} className={`mr-2 md:mr-4 opacity-40 hover:opacity-100 transition-opacity flex-shrink-0 ${selectedItems.has(item.id) ? 'text-amber-500 opacity-100' : theme.text}`}>
                          {selectedItems.has(item.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                        </button>
                        <div className="space-y-1 flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h4 className={`font-serif text-base md:text-lg truncate ${theme.text}`}>{item.title}</h4>
                            {statusFilter === 'all' && <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${item.status === 'draft' ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`}>{item.status}</span>}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${isDarkMode ? 'border-stone-800 text-stone-400' : 'border-stone-200 text-stone-500'}`}>
                              {item.category || 'Uncategorized'}
                            </span>
                            <p className={`text-[10px] font-bold uppercase tracking-widest opacity-30 truncate ${theme.text}`}>{item.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          <button onClick={() => handleNavigate('post', item)} className="p-2 md:p-3 opacity-40 hover:opacity-100 transition-opacity" title="Preview"><Eye size={18} /></button>
                          <button onClick={() => openEditPostEditor(item)} className="p-2 md:p-3 opacity-40 hover:opacity-100 transition-opacity"><Edit3 size={18} /></button>
                          <button onClick={() => { setItemToDelete({ id: item.id, name: item.title, type: 'post', collection: 'posts' }); setShowDeleteModal(true); }} className="p-2 md:p-3 opacity-40 hover:text-red-500 transition-opacity"><Trash2 size={18} /></button>
                        </div>
                      </div>
                    )) : (
                      <div className="p-20 text-center opacity-20"><Inbox size={48} className="mx-auto mb-4" /><p className="text-[10px] font-bold uppercase tracking-widest">No posts found.</p></div>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-6 mt-8 animate-in fade-in slide-in-from-bottom-2">
                      <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-full transition-all ${currentPage === 1 ? 'opacity-20 cursor-not-allowed' : `opacity-50 hover:opacity-100 ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}`}
                      >
                        <ChevronLeft size={20} className={theme.text} />
                      </button>
                      
                      <span className={`text-xs font-bold uppercase tracking-widest ${theme.text} opacity-60`}>
                        Page {currentPage} of {totalPages}
                      </span>

                      <button 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-full transition-all ${currentPage === totalPages ? 'opacity-20 cursor-not-allowed' : `opacity-50 hover:opacity-100 ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}`}
                      >
                        <ChevronRight size={20} className={theme.text} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {adminTab === 'quotes' && (
              <div className="space-y-8">
                {/* Search & Filters */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className={`flex-1 relative flex items-center px-5 py-3 rounded-full border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-200'}`}>
                    <Search size={16} className={`opacity-40 ${theme.text}`} />
                    <input
                      type="text"
                      value={quoteSearchQuery}
                      onChange={(e) => setQuoteSearchQuery(e.target.value)}
                      placeholder="Search quotes..."
                      className={`w-full bg-transparent border-none outline-none text-xs font-bold uppercase tracking-widest ml-3 ${theme.text} placeholder:opacity-30`}
                    />
                    {quoteSearchQuery && (
                      <button onClick={() => setQuoteSearchQuery('')} className="ml-2 opacity-40 hover:opacity-100"><X size={14} /></button>
                    )}
                  </div>

                  {quoteDisplayYears.length > 0 && (
                    <div className="relative">
                      <button onClick={() => setIsQuoteYearOpen(p => !p)} style={pillStyle} className={`h-full px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center justify-between gap-4 ${quoteSelectedYear !== 'All' ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900') : (isDarkMode ? 'bg-stone-900 border-stone-800 text-stone-400' : 'bg-white border-stone-200 text-stone-500')}`}>
                        <span>{quoteSelectedYear === 'All' ? 'Year' : quoteSelectedYear}</span>
                        <ChevronDown size={14} className={`${isQuoteYearOpen ? 'rotate-180' : ''} transition-transform`} />
                      </button>
                      {isQuoteYearOpen && (
                        <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300" onClick={() => setIsQuoteYearOpen(false)}>
                          <div className="absolute inset-0 bg-black/20 backdrop-blur-md" />
                          <div style={cardStyle} className={`relative w-full max-w-xs mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`} onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-col gap-2">
                              <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Year</p>
                              {['All', ...quoteDisplayYears].map(y => (
                                <button key={y} onClick={() => { setQuoteSelectedYear(y); setIsQuoteYearOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${quoteSelectedYear === y ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                                  <span>{y === 'All' ? 'All Years' : y}</span>
                                  <span className="opacity-40 text-[10px]">{quoteYearCounts[y] || 0}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {quoteDisplayMonths.length > 0 && (
                    <div className="relative">
                      <button onClick={() => setIsQuoteMonthOpen(p => !p)} style={pillStyle} className={`h-full px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center justify-between gap-4 ${quoteSelectedMonth !== 'All' ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900') : (isDarkMode ? 'bg-stone-900 border-stone-800 text-stone-400' : 'bg-white border-stone-200 text-stone-500')}`}>
                        <span>{quoteSelectedMonth === 'All' ? 'Month' : quoteSelectedMonth}</span>
                        <ChevronDown size={14} className={`${isQuoteMonthOpen ? 'rotate-180' : ''} transition-transform`} />
                      </button>
                      {isQuoteMonthOpen && (
                        <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300" onClick={() => setIsQuoteMonthOpen(false)}>
                          <div className="absolute inset-0 bg-black/20 backdrop-blur-md" />
                          <div style={cardStyle} className={`relative w-full max-w-xs mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`} onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-col gap-2">
                              <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Month</p>
                              {['All', ...quoteDisplayMonths].map(m => (
                                <button key={m} onClick={() => { setQuoteSelectedMonth(m); setIsQuoteMonthOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${quoteSelectedMonth === m ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                                  <span>{m === 'All' ? 'All Months' : m}</span>
                                  <span className="opacity-40 text-[10px]">{quoteMonthCounts[m] || 0}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {hasQuoteActiveFilters && (
                    <button onClick={handleResetQuoteFilters} style={pillStyle} className={`flex-shrink-0 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${isDarkMode ? 'border-red-900/30 text-red-400 hover:bg-red-900/20' : 'border-red-100 text-red-500 hover:bg-red-50'}`}>
                      <RefreshCw size={14} /> <span>Reset</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between px-2">
                  <h4 className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>
                    Filtered Quotes
                    <span className="ml-2 opacity-50">({filteredQuotes.length})</span>
                  </h4>
                </div>

                <div style={cardStyle} className={`border overflow-hidden divide-y ${isDarkMode ? 'bg-stone-900/20 border-stone-800 divide-stone-800' : 'bg-white border-stone-100 divide-stone-100 shadow-xl'}`}>
                  <div className={`px-6 py-4 flex items-center border-b ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
                    <button 
                      onClick={handleSelectAll}
                      className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity ${theme.text}`}
                    >
                      {paginatedQuotes.length > 0 && paginatedQuotes.every(i => selectedItems.has(i.id)) ? <CheckSquare size={14} /> : <Square size={14} />} Select All
                    </button>
                  </div>
                  {paginatedQuotes.length > 0 ? paginatedQuotes.map((item) => (
                    <div key={item.id} className="p-4 md:p-6 flex justify-between items-center hover:bg-stone-500/5 transition-colors gap-3">
                      <button onClick={(e) => { e.stopPropagation(); toggleSelection(item.id); }} className={`mr-2 md:mr-4 opacity-40 hover:opacity-100 transition-opacity flex-shrink-0 ${selectedItems.has(item.id) ? 'text-amber-500 opacity-100' : theme.text}`}>
                        {selectedItems.has(item.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                      </button>
                      <div className="space-y-1 flex-1 min-w-0">
                        <h4 className={`font-serif text-base md:text-lg line-clamp-1 truncate ${theme.text}`}>"{item.text || 'Empty Wisdom'}"</h4>
                        <p className={`text-[10px] font-bold uppercase tracking-widest opacity-30 truncate ${theme.text}`}>
                          {item.author || 'Ben Hasan'} • {item.date ? (safeParseDate(item.date) ? safeParseDate(item.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : item.date) : item.year}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <button title="Edit Quote" onClick={() => {
                          setEditingQuote(item); 
                          setEditQuoteText(item.text || ''); 
                          setEditQuoteAuthor(item.author || ''); 
                          setEditQuoteDate(item.date || ''); 
                          setEditQuoteTags(item.tags || ''); 
                          setShowEditQuoteForm(true);
                        }} className="p-2 md:p-3 opacity-40 hover:opacity-100 transition-opacity"><Edit3 size={18} /></button>
                        <button title="Delete Quote" onClick={() => { setItemToDelete({ id: item.id, name: `Quote by ${item.author || 'Unknown'}`, type: 'quote', collection: 'quotes' }); setShowDeleteModal(true); }} className="p-2 md:p-3 opacity-40 hover:text-red-500 transition-opacity"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  )) : (
                    <div className="p-20 text-center opacity-20"><Inbox size={48} className="mx-auto mb-4" /><p className="text-[10px] font-bold uppercase tracking-widest">No entries found.</p></div>
                  )}
                </div>

                {quoteTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-6 mt-8 animate-in fade-in slide-in-from-bottom-2">
                    <button 
                      onClick={() => setQuoteCurrentPage(p => Math.max(1, p - 1))}
                      disabled={quoteCurrentPage === 1}
                      className={`p-2 rounded-full transition-all ${quoteCurrentPage === 1 ? 'opacity-20 cursor-not-allowed' : `opacity-50 hover:opacity-100 ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}`}
                    >
                      <ChevronLeft size={20} className={theme.text} />
                    </button>
                    
                    <span className={`text-xs font-bold uppercase tracking-widest ${theme.text} opacity-60`}>
                      Page {quoteCurrentPage} of {quoteTotalPages}
                    </span>

                    <button 
                      onClick={() => setQuoteCurrentPage(p => Math.min(quoteTotalPages, p + 1))}
                      disabled={quoteCurrentPage === quoteTotalPages}
                      className={`p-2 rounded-full transition-all ${quoteCurrentPage === quoteTotalPages ? 'opacity-20 cursor-not-allowed' : `opacity-50 hover:opacity-100 ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}`}
                    >
                      <ChevronRight size={20} className={theme.text} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {adminTab === 'poems' && (
              <div className="space-y-8">
                {/* Search & Filters */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className={`flex-1 relative flex items-center px-5 py-3 rounded-full border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-200'}`}>
                    <Search size={16} className={`opacity-40 ${theme.text}`} />
                    <input
                      type="text"
                      value={poemSearchQuery}
                      onChange={(e) => setPoemSearchQuery(e.target.value)}
                      placeholder="Search poems..."
                      className={`w-full bg-transparent border-none outline-none text-xs font-bold uppercase tracking-widest ml-3 ${theme.text} placeholder:opacity-30`}
                    />
                    {poemSearchQuery && (
                      <button onClick={() => setPoemSearchQuery('')} className="ml-2 opacity-40 hover:opacity-100"><X size={14} /></button>
                    )}
                  </div>

                  {poemDisplayYears.length > 0 && (
                    <div className="relative">
                      <button 
                        onClick={() => setIsPoemYearOpen(!isPoemYearOpen)}
                        style={pillStyle}
                        className={`flex items-center justify-between gap-3 px-5 py-3 rounded-full border text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'bg-stone-950 border-stone-800' : 'bg-white border-stone-200'} ${theme.text}`}
                      >
                        <span>{poemSelectedYear === 'All' ? 'All Years' : poemSelectedYear}</span>
                        <ChevronDown size={14} />
                      </button>
                      {isPoemYearOpen && (
                        <div className={`absolute right-0 top-full mt-2 w-48 rounded-2xl border shadow-xl z-50 overflow-hidden divide-y ${isDarkMode ? 'bg-stone-900 border-stone-800 divide-stone-800' : 'bg-white border-stone-100 divide-stone-100'}`}>
                          <button onClick={() => { setPoemSelectedYear('All'); setIsPoemYearOpen(false); setPoemCurrentPage(1); }} className={`w-full px-5 py-3 text-left text-xs font-bold uppercase tracking-widest hover:bg-stone-500/10 ${theme.text} flex items-center justify-between`}>
                            <span>All Years</span>
                            {poemSelectedYear === 'All' && <CheckSquare size={12} />}
                          </button>
                          {poemDisplayYears.map(year => (
                            <button key={year} onClick={() => { setPoemSelectedYear(year); setIsPoemYearOpen(false); setPoemCurrentPage(1); }} className={`w-full px-5 py-3 text-left text-xs font-bold uppercase tracking-widest hover:bg-stone-500/10 ${theme.text} flex items-center justify-between`}>
                              <span>{year}</span>
                              <span className="opacity-40 font-mono text-[10px]">({poemYearCounts[year]})</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {poemDisplayMonths.length > 0 && (
                    <div className="relative">
                      <button 
                        onClick={() => setIsPoemMonthOpen(!isPoemMonthOpen)}
                        style={pillStyle}
                        className={`flex items-center justify-between gap-3 px-5 py-3 rounded-full border text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'bg-stone-950 border-stone-800' : 'bg-white border-stone-200'} ${theme.text}`}
                      >
                        <span>{poemSelectedMonth === 'All' ? 'All Months' : poemSelectedMonth}</span>
                        <ChevronDown size={14} />
                      </button>
                      {isPoemMonthOpen && (
                        <div className={`absolute right-0 top-full mt-2 w-48 rounded-2xl border shadow-xl z-50 overflow-hidden divide-y ${isDarkMode ? 'bg-stone-900 border-stone-800 divide-stone-800' : 'bg-white border-stone-100 divide-stone-100'}`}>
                          <button onClick={() => { setPoemSelectedMonth('All'); setIsPoemMonthOpen(false); setPoemCurrentPage(1); }} className={`w-full px-5 py-3 text-left text-xs font-bold uppercase tracking-widest hover:bg-stone-500/10 ${theme.text} flex items-center justify-between`}>
                            <span>All Months</span>
                            {poemSelectedMonth === 'All' && <CheckSquare size={12} />}
                          </button>
                          {poemDisplayMonths.map(month => (
                            <button key={month} onClick={() => { setPoemSelectedMonth(month); setIsPoemMonthOpen(false); setPoemCurrentPage(1); }} className={`w-full px-5 py-3 text-left text-xs font-bold uppercase tracking-widest hover:bg-stone-500/10 ${theme.text} flex items-center justify-between`}>
                              <span>{month}</span>
                              <span className="opacity-40 font-mono text-[10px]">({poemMonthCounts[month]})</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {hasPoemActiveFilters && (
                    <button 
                      onClick={handleResetPoemFilters}
                      style={pillStyle}
                      className="px-6 py-3 border border-red-500/20 text-red-500 hover:bg-red-500/10 text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2"
                    >
                      <X size={14} /> Clear
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between px-2">
                  <h4 className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>
                    Filtered Poems
                    <span className="ml-2 opacity-50">({filteredPoems.length})</span>
                  </h4>
                </div>

                <div style={cardStyle} className={`border overflow-hidden divide-y ${isDarkMode ? 'bg-stone-900/20 border-stone-800 divide-stone-800' : 'bg-white border-stone-100 divide-stone-100 shadow-xl'}`}>
                  <div className={`px-6 py-4 flex items-center border-b ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
                    <button 
                      onClick={handleSelectAll}
                      className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity ${theme.text}`}
                    >
                      {paginatedPoems.length > 0 && paginatedPoems.every(i => selectedItems.has(i.id)) ? <CheckSquare size={14} /> : <Square size={14} />} Select All
                    </button>
                  </div>
                  {paginatedPoems.length > 0 ? paginatedPoems.map((item) => (
                    <div key={item.id} className="p-4 md:p-6 flex justify-between items-center hover:bg-stone-500/5 transition-colors gap-3">
                      <button onClick={(e) => { e.stopPropagation(); toggleSelection(item.id); }} className={`mr-2 md:mr-4 opacity-40 hover:opacity-100 transition-opacity flex-shrink-0 ${selectedItems.has(item.id) ? 'text-amber-500 opacity-100' : theme.text}`}>
                        {selectedItems.has(item.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                      </button>
                      <div className="space-y-1 flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h4 className={`font-serif text-base md:text-lg line-clamp-1 truncate ${theme.text}`}>{item.title || 'Untitled Poem'}</h4>
                          <span className={`text-[9px] font-mono opacity-50 ${theme.textMuted}`}>{item.text?.length || 0} chars</span>
                        </div>
                        <p className={`text-[11px] opacity-75 line-clamp-1 italic ${theme.textMuted} whitespace-nowrap overflow-hidden text-ellipsis max-w-xl`}>
                          {item.text}
                        </p>
                        <p className={`text-[10px] font-bold uppercase tracking-widest opacity-30 truncate ${theme.text}`}>
                          {item.author || 'Ben Hasan'} • {item.date ? (safeParseDate(item.date) ? safeParseDate(item.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : item.date) : item.year}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <button title="Edit Poem" onClick={() => {
                          setEditingPoem(item); 
                          setEditPoemTitle(item.title || '');
                          setEditPoemText(item.text || ''); 
                          setEditPoemAuthor(item.author || ''); 
                          setEditPoemDate(item.date || ''); 
                          setEditPoemTags(item.tags || ''); 
                          setShowEditPoemForm(true);
                        }} className="p-2 md:p-3 opacity-40 hover:opacity-100 transition-opacity"><Edit3 size={18} /></button>
                        <button title="Delete Poem" onClick={() => { setItemToDelete({ id: item.id, name: item.title || `Poem by ${item.author || 'Unknown'}`, type: 'poem', collection: 'poems' }); setShowDeleteModal(true); }} className="p-2 md:p-3 opacity-40 hover:text-red-500 transition-opacity"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  )) : (
                    <div className="p-20 text-center opacity-20"><Inbox size={48} className="mx-auto mb-4" /><p className="text-[10px] font-bold uppercase tracking-widest">No entries found.</p></div>
                  )}
                </div>

                {poemTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-6 mt-8 animate-in fade-in slide-in-from-bottom-2">
                    <button 
                      onClick={() => setPoemCurrentPage(p => Math.max(1, p - 1))}
                      disabled={poemCurrentPage === 1}
                      className={`p-2 rounded-full transition-all ${poemCurrentPage === 1 ? 'opacity-20 cursor-not-allowed' : `opacity-50 hover:opacity-100 ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}`}
                    >
                      <ChevronLeft size={20} className={theme.text} />
                    </button>
                    
                    <span className={`text-xs font-bold uppercase tracking-widest ${theme.text} opacity-60`}>
                      Page {poemCurrentPage} of {poemTotalPages}
                    </span>

                    <button 
                      onClick={() => setPoemCurrentPage(p => Math.min(poemTotalPages, p + 1))}
                      disabled={poemCurrentPage === poemTotalPages}
                      className={`p-2 rounded-full transition-all ${poemCurrentPage === poemTotalPages ? 'opacity-20 cursor-not-allowed' : `opacity-50 hover:opacity-100 ${isDarkMode ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}`}
                    >
                      <ChevronRight size={20} className={theme.text} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {adminTab === 'diary' && (
              <div className="space-y-8">
                {/* Status Filter Chips */}
                <div className="flex flex-wrap items-center gap-4">
                  <button onClick={() => { setDiaryStatusFilter('all'); setDiaryCurrentPage(1); }}
                    style={pillStyle}
                    className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                      diaryStatusFilter === 'all'
                        ? (isDarkMode ? 'bg-stone-800 border-stone-800 text-white' : 'bg-stone-900 border-stone-900 text-white')
                        : `border-stone-200 dark:border-stone-800 opacity-60 hover:opacity-100 ${theme.text}`
                    }`}>
                    All <span className="ml-2 opacity-60">{diary.length}</span>
                  </button>
                  <button onClick={() => { setDiaryStatusFilter('published'); setDiaryCurrentPage(1); }}
                    style={pillStyle}
                    className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                      diaryStatusFilter === 'published'
                        ? (isDarkMode ? 'bg-stone-800 border-stone-800 text-white' : 'bg-stone-900 border-stone-900 text-white')
                        : `border-stone-200 dark:border-stone-800 opacity-60 hover:opacity-100 ${theme.text}`
                    }`}>
                    Published <span className="ml-2 opacity-60">{diary.filter(d => (d.status || 'published') === 'published').length}</span>
                  </button>
                  <button onClick={() => { setDiaryStatusFilter('draft'); setDiaryCurrentPage(1); }}
                    style={pillStyle}
                    className={`px-5 py-2 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                      diaryStatusFilter === 'draft'
                        ? (isDarkMode ? 'bg-stone-800 border-stone-800 text-white' : 'bg-stone-900 border-stone-900 text-white')
                        : `border-stone-200 dark:border-stone-800 opacity-60 hover:opacity-100 ${theme.text}`
                    }`}>
                    Drafts <span className="ml-2 opacity-60">{diary.filter(d => d.status === 'draft').length}</span>
                  </button>
                </div>

                {/* Search & Filters */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className={`flex-1 relative flex items-center px-5 py-3 rounded-full border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-200'}`}>
                    <Search size={16} className={`opacity-40 ${theme.text}`} />
                    <input
                      type="text"
                      value={diarySearchQuery}
                      onChange={(e) => setDiarySearchQuery(e.target.value)}
                      placeholder="Search diary entries..."
                      className={`w-full bg-transparent border-none outline-none text-xs font-bold uppercase tracking-widest ml-3 ${theme.text} placeholder:opacity-30`}
                    />
                    {diarySearchQuery && (
                      <button onClick={() => setDiarySearchQuery('')} className="ml-2 opacity-40 hover:opacity-100"><X size={14} /></button>
                    )}
                  </div>

                  {diaryDisplayYears.length > 0 && (
                    <div className="relative">
                      <button 
                        onClick={() => setIsDiaryYearOpen(!isDiaryYearOpen)}
                        style={pillStyle}
                        className={`h-full px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center justify-between gap-4 ${diarySelectedYear !== 'All' ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900') : (isDarkMode ? 'bg-stone-900 border-stone-800 text-stone-400' : 'bg-white border-stone-200 text-stone-500')}`}
                      >
                        <span>{diarySelectedYear === 'All' ? 'Year' : diarySelectedYear}</span>
                        <ChevronDown size={14} className="opacity-40" />
                      </button>
                      
                      {isDiaryYearOpen && (
                        <div className={`absolute z-30 right-0 mt-3 w-56 border shadow-2xl overflow-hidden divide-y backdrop-blur-md ${isDarkMode ? 'bg-stone-950/95 border-stone-800 divide-stone-800' : 'bg-white/95 border-stone-200 divide-stone-200'}`}>
                          <button onClick={() => { setDiarySelectedYear('All'); setIsDiaryYearOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${diarySelectedYear === 'All' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                            <span>All Years</span>
                            <span className="opacity-50 font-mono text-[10px]">{diaryYearCounts['All'] || 0}</span>
                          </button>
                          {diaryDisplayYears.map(y => (
                            <button key={y} onClick={() => { setDiarySelectedYear(y); setIsDiaryYearOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${diarySelectedYear === y ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                              <span>{y}</span>
                              <span className="opacity-50 font-mono text-[10px]">{diaryYearCounts[y] || 0}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {diaryDisplayMonths.length > 0 && (
                    <div className="relative">
                      <button 
                        onClick={() => setIsDiaryMonthOpen(!isDiaryMonthOpen)}
                        style={pillStyle}
                        className={`h-full px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center justify-between gap-4 ${diarySelectedMonth !== 'All' ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900') : (isDarkMode ? 'bg-stone-900 border-stone-800 text-stone-400' : 'bg-white border-stone-200 text-stone-500')}`}
                      >
                        <span>{diarySelectedMonth === 'All' ? 'Month' : diarySelectedMonth}</span>
                        <ChevronDown size={14} className="opacity-40" />
                      </button>
                      
                      {isDiaryMonthOpen && (
                        <div className={`absolute z-30 right-0 mt-3 w-56 border shadow-2xl overflow-hidden divide-y backdrop-blur-md ${isDarkMode ? 'bg-stone-950/95 border-stone-800 divide-stone-800' : 'bg-white/95 border-stone-200 divide-stone-200'}`}>
                          <button onClick={() => { setDiarySelectedMonth('All'); setIsDiaryMonthOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${diarySelectedMonth === 'All' ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                            <span>All Months</span>
                            <span className="opacity-50 font-mono text-[10px]">{diaryMonthCounts['All'] || 0}</span>
                          </button>
                          {diaryDisplayMonths.map(m => (
                            <button key={m} onClick={() => { setDiarySelectedMonth(m); setIsDiaryMonthOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${diarySelectedMonth === m ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                              <span>{m}</span>
                              <span className="opacity-50 font-mono text-[10px]">{diaryMonthCounts[m] || 0}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {hasDiaryActiveFilters && (
                    <button 
                      onClick={handleResetDiaryFilters}
                      style={pillStyle}
                      className="px-6 py-3 border border-red-500/20 text-red-500 hover:bg-red-500/10 text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2"
                    >
                      <X size={14} /> Clear
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between px-2">
                  <h4 className={`text-[10px] font-bold uppercase tracking-widest opacity-40 ${theme.text}`}>
                    {diaryBooks.length} {diaryBooks.length === 1 ? 'Book' : 'Books'}
                    <span className="ml-2 opacity-50">({filteredDiary.length} entries)</span>
                  </h4>
                </div>

                {diaryBooks.length > 0 ? (
                  <div style={cardStyle} className={`border overflow-hidden ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                    {diaryBooks.map((book) => {
                      const isExpanded = expandedBooks.has(book.key);
                      return (
                        <div key={book.key} className={`border-b last:border-b-0 ${isDarkMode ? 'border-stone-800' : 'border-stone-100'}`}>
                          {/* Book Section Header */}
                          <button
                            onClick={() => {
                              const next = new Set(expandedBooks);
                              if (next.has(book.key)) next.delete(book.key); else next.add(book.key);
                              setExpandedBooks(next);
                            }}
                            className={`w-full flex items-center justify-between px-5 py-3 transition-all hover:bg-stone-500/5 ${theme.text}`}
                          >
                            <div className="flex items-center gap-4 min-w-0 flex-1 text-left">
                              <ChevronRight size={14} className={`flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''} opacity-40`} />
                              <div className="min-w-0 flex-1">
                                <h4 className={`font-serif text-base md:text-lg truncate ${theme.text}`}>{book.title}</h4>
                                <p className={`text-[10px] font-bold uppercase tracking-widest opacity-30 mt-0.5 ${theme.text}`}>
                                  {book.author} · {book.entries.length} {book.entries.length === 1 ? 'entry' : 'entries'}
                                  {book.topicCount > 0 ? ` · ${book.topicCount} ${book.topicCount === 1 ? 'topic' : 'topics'}` : ''}
                                </p>
                              </div>
                            </div>
                            <span className={`flex-shrink-0 ml-4 text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                              isDarkMode ? 'bg-stone-800 text-stone-400' : 'bg-stone-100 text-stone-500'
                            }`}>
                              {book.type === 'book' ? 'Book' : 'Article'}
                            </span>
                          </button>

                          {/* Entries for this book */}
                          {isExpanded && (
                            <div className={`divide-y ${isDarkMode ? 'divide-stone-800/50' : 'divide-stone-100'}`}>
                              {book.entries.map((entry) => (
                                <div key={entry.id} className="pl-14 pr-5 py-3 flex items-center justify-between gap-3 hover:bg-stone-500/5 transition-colors">
                                  <div className="space-y-1 flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                      {entry.topic && (
                                        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 opacity-50 ${theme.text}`}>
                                          {entry.topic}
                                        </span>
                                      )}
                                      <span className={`text-[9px] font-mono px-3 py-1 rounded-full uppercase tracking-widest ${
                                        isDarkMode ? 'bg-stone-800 text-stone-400' : 'bg-stone-100 text-stone-500'
                                      }`}>
                                        {entry.status === 'draft' ? 'Draft' : (entry.type === 'book' ? 'Book' : 'Article')}
                                      </span>
                                    </div>
                                    <p className={`text-[11px] opacity-75 line-clamp-1 italic ${theme.textMuted} whitespace-nowrap overflow-hidden text-ellipsis max-w-xl`}>
                                      {entry.content ? (entry.content.replace(/<[^>]+>/g, '').substring(0, 80) + (entry.content.replace(/<[^>]+>/g, '').length > 80 ? '...' : '')) : ''}
                                    </p>
                                    <p className={`text-[10px] font-bold uppercase tracking-widest opacity-30 truncate ${theme.text}`}>
                                      {entry.date ? (safeParseDate(entry.date) ? safeParseDate(entry.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : entry.date) : ''}
                                    </p>
                                  </div>
                                  <div className="flex items-center space-x-1 flex-shrink-0">
                                    <button title="Edit" onClick={() => {
                                      setEditingDiary(entry);
                                      diaryEditor.openEditDiaryEditor(entry);
                                      setShowDiaryModal(true);
                                    }} className="p-2 opacity-40 hover:opacity-100 transition-opacity"><Edit3 size={16} /></button>
                                    <button title="Delete" onClick={() => { setItemToDelete({ id: entry.id, name: entry.title || 'Reflection', type: 'diary', collection: 'diary' }); setShowDeleteModal(true); }} className="p-2 opacity-40 hover:text-red-500 transition-opacity"><Trash2 size={16} /></button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={cardStyle} className={`border p-20 text-center opacity-20 ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                    <Inbox size={48} className="mx-auto mb-4" />
                    <p className="text-[10px] font-bold uppercase tracking-widest">No entries found.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {adminTab === 'ideas' && (
          <div className="animate-in fade-in duration-700 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className={`text-xl font-serif ${theme.text}`}>Bank Idea</h3>
              <button
                onClick={() => { setShowIdeaEditor(true); setEditingIdea(null); }}
                style={pillStyle}
                className="px-5 py-2.5 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 hover:opacity-80"
              >
                <Plus size={14} /> Create Mind Map
              </button>
            </div>

            {/* Gemini API Configuration */}
            <div style={cardStyle} className={`p-5 border flex flex-col md:flex-row md:items-center justify-between gap-4 ${isDarkMode ? 'border-stone-850 bg-stone-900/30' : 'border-stone-100 bg-stone-50'}`}>
              <div className="space-y-1">
                <h4 className={`text-xs font-bold uppercase tracking-widest ${theme.text}`}>Gemini AI Configuration</h4>
                <p className="text-[10px] opacity-50">Set your Gemini API Key in browser storage for auto-categorization suggestions.</p>
              </div>
              <div className="flex items-center gap-2 max-w-sm w-full">
                <input 
                  type="password"
                  placeholder="Gemini API Key..."
                  value={geminiKey}
                  onChange={(e) => {
                    localStorage.setItem('gemini_api_key', e.target.value);
                    setGeminiKey(e.target.value);
                  }}
                  className={`flex-1 px-4 py-2 text-xs border outline-none rounded-full ${isDarkMode ? 'bg-stone-950 border-stone-800 text-white' : 'bg-white border-stone-200 text-stone-900'}`}
                />
              </div>
            </div>

            {ideas.length === 0 ? (
              <div style={cardStyle} className={`border p-20 text-center opacity-20 ${isDarkMode ? 'bg-stone-900/20 border-stone-800' : 'bg-white border-stone-100 shadow-xl'}`}>
                <Lightbulb size={48} className="mx-auto mb-4" />
                <p className="text-[10px] font-bold uppercase tracking-widest">No mind maps yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {ideas.sort((a, b) => (b.updatedAt || b.createdAt) - (a.updatedAt || a.createdAt)).map((idea) => (
                  <div key={idea.id} style={cardStyle} className={`flex items-center justify-between p-5 border transition-colors ${isDarkMode ? 'border-stone-800 bg-stone-900/20 hover:bg-stone-800/40' : 'border-stone-100 bg-white hover:shadow-md'}`}>
                    <div className="space-y-1 min-w-0 flex-1">
                      <h4 className={`font-serif text-base truncate ${theme.text}`}>{idea.title || 'Untitled'}</h4>
                      <div className="flex items-center gap-3">
                        <span className={`text-[9px] font-bold uppercase tracking-widest ${idea.status === 'published' ? 'text-emerald-500' : 'text-amber-500'}`}>
                          {idea.status || 'draft'}
                        </span>
                        <span className={`text-[9px] opacity-40 ${theme.text}`}>
                          {idea.nodes?.length || 0} nodes
                        </span>
                        {idea.category && (
                          <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-stone-800 text-stone-300' : 'bg-stone-100 text-stone-600'}`}>
                            {idea.category}
                          </span>
                        )}
                        {idea.date && (
                          <span className={`text-[9px] opacity-40 flex items-center gap-1 ${theme.text}`}>
                            <Calendar size={10} /> {formatDateToDDMMYYYY(idea.date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <button onClick={() => { setEditingIdea(idea); setShowIdeaEditor(true); }} className="p-2 opacity-40 hover:opacity-100 transition-opacity" title="Edit"><Edit3 size={16} /></button>
                      <button onClick={() => { setItemToDelete({ id: idea.id, name: idea.title || 'Mind Map', type: 'ideas', collection: 'ideas' }); setShowDeleteModal(true); }} className="p-2 opacity-40 hover:text-red-500 transition-opacity" title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        </main>

      {/* Floating Modals and Bars */}
        {/* Bulk Actions Floating Bar */}
        {selectedItems.size > 0 && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-6 py-3 bg-stone-900 text-white rounded-full shadow-2xl animate-in slide-in-from-bottom-4 border border-stone-800">
            <span className="text-xs font-bold uppercase tracking-widest">{selectedItems.size} Selected</span>
            <div className="w-px h-4 bg-stone-700" />
            <button 
              onClick={() => {
                let bulkType = 'bulk';
                let coll = 'posts';
                if (adminTab === 'quotes') coll = 'quotes';
                if (adminTab === 'poems') coll = 'poems';
                if (adminTab === 'diary') coll = 'diary';
                if (adminTab === 'analytics') {
                  bulkType = 'bulk_analytics';
                  coll = 'page_views';
                }
                setItemToDelete({ id: 'bulk', name: `${selectedItems.size} items`, type: bulkType, collection: coll });
                setShowDeleteModal(true);
              }} 
              className="text-red-400 hover:text-red-300 flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-colors"
            >
              <Trash2 size={14} /> Delete
            </button>
            <button onClick={() => setSelectedItems(new Set())} className="opacity-50 hover:opacity-100 transition-opacity ml-2"><X size={14} /></button>
            <div className="w-px h-4 bg-stone-700" />
            <button onClick={() => setSelectedItems(new Set())} className="opacity-50 hover:opacity-100 flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-opacity">
              <X size={14} /> Clear
            </button>
          </div>
        )}

        <QuoteEditorModal 
          isOpen={showQuoteForm || showEditQuoteForm} 
          mode={showEditQuoteForm ? 'edit' : 'add'} 
          onClose={() => { setShowQuoteForm(false); setShowEditQuoteForm(false); }} 
          onSave={showEditQuoteForm ? async () => { 
            setIsSaving(true); 
            try { 
              await updateQuote(editingQuote.id, { 
                text: editQuoteText, 
                author: editQuoteAuthor, 
                date: editQuoteDate, 
                tags: editQuoteTags 
              }); 
              setShowEditQuoteForm(false); 
              showToast("Quote updated"); 
            } catch (error) {
              console.error("Failed to update quote:", error);
              showToast("Error updating quote. Check console.", "error");
            } finally { 
              setIsSaving(false); 
            } 
          } : async () => {
            setIsSaving(true);
            try {
              await submitQuote({ 
                text: quoteText, 
                author: quoteAuthor, 
                date: quoteDate, 
                tags: quoteTags 
              });
              setShowQuoteForm(false);
              setQuoteText('');
              setQuoteAuthor('');
              setQuoteDate('');
              setQuoteTags('');
              showToast("Quote created");
            } catch (error) {
              console.error("Failed to create quote:", error);
              showToast("Error creating quote. Check console.", "error");
            } finally {
              setIsSaving(false);
            }
          }} 
          text={showEditQuoteForm ? editQuoteText : quoteText} 
          setText={showEditQuoteForm ? setEditQuoteText : setQuoteText} 
          author={showEditQuoteForm ? editQuoteAuthor : quoteAuthor} 
          setAuthor={showEditQuoteForm ? setEditQuoteAuthor : setQuoteAuthor}
          date={showEditQuoteForm ? editQuoteDate : quoteDate}
          setDate={showEditQuoteForm ? setEditQuoteDate : setQuoteDate}
          tags={showEditQuoteForm ? editQuoteTags : quoteTags}
          setTags={showEditQuoteForm ? setEditQuoteTags : setQuoteTags}
          isSaving={isSaving} 
          isDarkMode={isDarkMode} 
        />
        <PoemEditorModal 
          isOpen={showPoemForm || showEditPoemForm} 
          mode={showEditPoemForm ? 'edit' : 'add'} 
          onClose={() => { 
            setShowPoemForm(false); 
            setShowEditPoemForm(false); 
            setEditingPoem(null); 
            setPoemTitle(''); 
            setPoemText(''); 
            setPoemAuthor(''); 
            setPoemDate(''); 
            setPoemTags(''); 
            setEditPoemTitle(''); 
            setEditPoemText(''); 
            setEditPoemAuthor(''); 
            setEditPoemDate(''); 
            setEditPoemTags(''); 
          }} 
          onSave={showEditPoemForm ? async () => {
            setIsSaving(true);
            try {
              await updatePoem(editingPoem.id, { 
                title: editPoemTitle, 
                text: editPoemText, 
                author: editPoemAuthor, 
                date: editPoemDate, 
                tags: editPoemTags 
              });
              setShowEditPoemForm(false);
              setEditingPoem(null);
              showToast("Poem updated");
            } catch (error) {
              console.error("Failed to update poem:", error);
              showToast("Error updating poem. Check console.", "error");
            } finally {
              setIsSaving(false);
            }
          } : async () => {
            setIsSaving(true);
            try {
              await submitPoem({ 
                title: poemTitle, 
                text: poemText, 
                author: poemAuthor, 
                date: poemDate, 
                tags: poemTags 
              });
              setShowPoemForm(false);
              setPoemTitle('');
              setPoemText('');
              setPoemAuthor('');
              setPoemDate('');
              setPoemTags('');
              showToast("Poem created");
            } catch (error) {
              console.error("Failed to create poem:", error);
              showToast("Error creating poem. Check console.", "error");
            } finally {
              setIsSaving(false);
            }
          }}
          title={showEditPoemForm ? editPoemTitle : poemTitle}
          setTitle={showEditPoemForm ? setEditPoemTitle : setPoemTitle}
          text={showEditPoemForm ? editPoemText : poemText}
          setText={showEditPoemForm ? setEditPoemText : setPoemText}
          author={showEditPoemForm ? editPoemAuthor : poemAuthor}
          setAuthor={showEditPoemForm ? setEditPoemAuthor : setPoemAuthor}
          date={showEditPoemForm ? editPoemDate : poemDate}
          setDate={showEditPoemForm ? setEditPoemDate : setPoemDate}
          tags={showEditPoemForm ? editPoemTags : poemTags}
          setTags={showEditPoemForm ? setEditPoemTags : setPoemTags}
          isSaving={isSaving}
          isDarkMode={isDarkMode}
        />
        {adminTab === 'diary' && (<>
        <DiaryEditorModal
          isOpen={showDiaryModal}
          onClose={() => { setShowDiaryModal(false); setEditingDiary(null); diaryEditor.closeEditor(); }}
          isDarkMode={isDarkMode}
          editor={diaryEditor.editor}
          executeCommand={diaryEditor.executeCommand}
          liveStats={diaryEditor.liveStats}
          saveStatus={diaryEditor.saveStatus}
          composeTitle={diaryEditor.composeTitle} setComposeTitle={diaryEditor.setComposeTitle}
          composeAuthor={diaryEditor.composeAuthor} setComposeAuthor={diaryEditor.setComposeAuthor}
          composeType={diaryEditor.composeType} setComposeType={diaryEditor.setComposeType}
          composeDate={diaryEditor.composeDate} setComposeDate={diaryEditor.setComposeDate}
          composeTopic={diaryEditor.composeTopic} setComposeTopic={diaryEditor.setComposeTopic}
          composePageNumber={diaryEditor.composePageNumber} setComposePageNumber={diaryEditor.setComposePageNumber}
          composeLink={diaryEditor.composeLink} setComposeLink={diaryEditor.setComposeLink}
          composeCategory={diaryEditor.composeCategory} setComposeCategory={diaryEditor.setComposeCategory}
          handleSaveDiary={diaryEditor.handleSaveDiary}
          localDraftAvailable={diaryEditor.localDraftAvailable}
          restoreLocalDraft={diaryEditor.restoreLocalDraft}
          clearLocalDraft={diaryEditor.clearLocalDraft}
          grammarIssues={diaryEditor.grammarIssues}
          isCheckingGrammar={diaryEditor.isCheckingGrammar}
          handleGrammarCheck={diaryEditor.handleGrammarCheck}
          replaceGrammarFix={diaryEditor.replaceGrammarFix}
          clearGrammarIssues={diaryEditor.clearGrammarIssues}
          ignoreGrammarIssue={diaryEditor.ignoreGrammarIssue}
          categories={categories}
          diary={diary}
        />
        </>)}
          <DeleteConfirmationModal 
          isOpen={showDeleteModal}
          onClose={() => { setShowDeleteModal(false); setItemToDelete(null); }} 
          onConfirm={async () => { 
            if (itemToDelete.type === 'bulk') {
              const ids = Array.from(selectedItems);
              await bulkDelete(ids, itemToDelete.collection);
              if (itemToDelete.collection === 'posts') setPosts(prev => prev.filter(p => !selectedItems.has(p.id)));
              else if (itemToDelete.collection === 'poems') setPoems(prev => prev.filter(p => !selectedItems.has(p.id)));
              else if (itemToDelete.collection === 'diary') setDiary(prev => prev.filter(d => !selectedItems.has(d.id)));
              else setQuotes(prev => prev.filter(q => !selectedItems.has(q.id)));
              setSelectedItems(new Set());
              showToast(`Deleted ${ids.length} items.`);
            } else if (itemToDelete.type === 'bulk_analytics') {
              const ids = Array.from(selectedItems);
              for (const id of ids) {
                await deleteDoc(doc(db, 'page_views', id));
              }
              setAnalyticsData(prev => prev.filter(a => !selectedItems.has(a.id)));
              setSelectedItems(new Set());
              showToast(`Deleted ${ids.length} records.`);
            } else if (itemToDelete.type === 'analytics') {
              await deleteDoc(doc(db, 'page_views', itemToDelete.id));
              setAnalyticsData(prev => prev.filter(item => item.id !== itemToDelete.id));
              showToast("Record deleted.");
            } else {
              await handleDeleteWithConfirm(itemToDelete.id, itemToDelete.collection); 
              if (itemToDelete.type === 'post') setPosts(prev => prev.filter(p => p.id !== itemToDelete.id)); 
              else if (itemToDelete.type === 'poem') setPoems(prev => prev.filter(p => p.id !== itemToDelete.id)); 
              else if (itemToDelete.type === 'diary') setDiary(prev => prev.filter(d => d.id !== itemToDelete.id));
              else setQuotes(prev => prev.filter(q => q.id !== itemToDelete.id)); 
              showToast("Successfully removed."); 
            }
            setShowDeleteModal(false); 
          }} 
          itemName={itemToDelete?.name} 
          itemType={itemToDelete?.type} 
          isDarkMode={isDarkMode} 
        />
        {restoreConfirmBackup && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-md" onClick={() => !isRestoring && setRestoreConfirmBackup(null)} />
            <div style={cardStyle} className={`relative w-full max-w-md p-10 shadow-2xl border ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
              <div className="text-center space-y-6">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center bg-amber-500/10">
                  {isRestoring ? (
                    <Loader2 size={32} className="text-amber-500 animate-spin" />
                  ) : (
                    <RotateCcw size={32} className="text-amber-500" />
                  )}
                </div>
                <div className="space-y-2">
                  <h3 className={`text-2xl font-serif font-bold ${theme.text}`}>Confirm Overwrite & Restore</h3>
                  <p className={`text-xs opacity-60 leading-relaxed ${theme.textMuted}`}>
                    You are about to restore your live database to the snapshot:<br />
                    <span className="font-mono text-[10px] font-bold text-amber-500 break-all">{restoreConfirmBackup.name}</span>
                  </p>
                  <p className="text-[10px] text-red-500 font-bold uppercase tracking-wider bg-red-500/10 py-2 px-4 rounded-xl border border-red-500/20">
                    ⚠️ WARNING: This will permanently overwrite all active reflections, poetry studio verses, and quotes. This action is irreversible.
                  </p>
                </div>
                <div className="flex flex-col gap-3 pt-4">
                  <button 
                    onClick={() => handleRestoreBackup(restoreConfirmBackup)}
                    disabled={isRestoring}
                    style={pillStyle}
                    className="w-full py-4 bg-amber-500 text-stone-950 text-[10px] font-bold uppercase tracking-[0.2em] hover:opacity-90 disabled:opacity-50 transition-all shadow-xl flex items-center justify-center gap-2"
                  >
                    {isRestoring && <Loader2 size={12} className="animate-spin" />}
                    <span>{isRestoring ? 'Restoring...' : 'Confirm Overwrite'}</span>
                  </button>
                  <button 
                    onClick={() => setRestoreConfirmBackup(null)}
                    disabled={isRestoring}
                    style={pillStyle}
                    className={`w-full py-4 border text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${isDarkMode ? 'border-stone-800 text-stone-400 hover:bg-stone-800' : 'border-stone-100 text-stone-500 hover:bg-stone-50'}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {restoreConfirmBackup && (
          <div>...</div>
        )}

        {/* Mind Map Editor Modal */}
        {showIdeaEditor && (
          <MindMapEditor
            isDarkMode={isDarkMode}
            theme={theme}
            initialMap={editingIdea}
            saveStatus={ideaSaveStatus}
            onSave={async (mapData, editingId) => {
              setIdeaSaveStatus('saving');
              try {
                const res = await saveIdea(mapData, editingId);
                if (res.success) {
                  setShowIdeaEditor(false);
                  setEditingIdea(null);
                  showToast('Mind map saved!', 'success');
                } else {
                  showToast(`Save failed: ${res.error}`, 'error');
                }
              } catch (e) {
                showToast(`Save failed: ${e.message}`, 'error');
              }
              setIdeaSaveStatus('idle');
            }}
            onClose={() => { setShowIdeaEditor(false); setEditingIdea(null); }}
          />
        )}

        {toast && <Toast message={toast.message} type={toast.type} isDarkMode={isDarkMode} onClose={() => setToast(null)} />}
    </section>
  );
};

export default AdminSection;